import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../hooks/useAuth';
import tareoService from '../services/tareoService';
import autorizacionService from '../services/autorizacionService';
import configuracionInicialVacacionesService from '../services/configuracionInicialVacacionesService';
import ImportadorSaldosVacaciones from '../components/ImportadorSaldosVacaciones';
import { obtenerFirmaEmpresaBase64 } from '../services/firmaEmpresaService';
import { jsPDF } from 'jspdf';
import { empleadoActivoEnFechaPlameOInterno } from '../utils/fechaImportacion';

/** Recibo por Honorarios (RH) — usa tipo_contratacion (igual que Empleados) */
const esReciboHonorarios = (empleado) => {
  const tipo = String(empleado?.tipo_contratacion || '').trim().toLowerCase();
  if (!tipo) return false;
  return tipo.includes('honorario') || tipo === 'rh' || tipo === 'recibo por honorarios';
};

function formatoImagenFirma(dataUri) {
  if (!dataUri) return 'PNG';
  if (/image\/jpe?g/i.test(dataUri)) return 'JPEG';
  return 'PNG';
}

const Autorizaciones = () => {
  const { selectedCompany } = useAuth();
  
  const [activeTab, setActiveTab] = useState('horas-extra');
  const [vacationSubTab, setVacationSubTab] = useState('solicitudes');
  const [showModal, setShowModal] = useState(false);
  const [selectedAuthorization, setSelectedAuthorization] = useState(null);
  const [newAuthorization, setNewAuthorization] = useState({
    employeeId: '',
    type: 'horas-extra',
    subType: 'horas-extra-25', // Subtipo para horas extra (25% o 35%)
    startDate: '',
    endDate: '',
    hours: '',
    days: '',
    reason: '',
    description: '',
    status: 'pendiente',
    diasReposoMedico: [] // Para días específicos de reposo médico (0 horas)
  });

  // Estados para filtros
  const [filters, setFilters] = useState({
    dateType: 'month', // 'day', 'week', 'month', 'year' - Por defecto mes
    selectedDate: new Date().toISOString().split('T')[0],
    employeeName: '',
    status: 'all' // 'all', 'pendiente', 'aprobado', 'rechazado'
  });

  // Estados para backend
  const [employees, setEmployees] = useState([]);
  const [authorizations, setAuthorizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingAuthorizations, setLoadingAuthorizations] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Estados para saldos iniciales
  const [initialBalances, setInitialBalances] = useState({}); // {employeeId: dias}
  const [editingBalance, setEditingBalance] = useState(null); // ID del empleado siendo editado
  const [tempBalance, setTempBalance] = useState(''); // Valor temporal del input
  const [loadingInitialBalances, setLoadingInitialBalances] = useState(false); // Estado de carga para saldos iniciales
  const [importadorSaldosOpen, setImportadorSaldosOpen] = useState(false);
  /** planilla | recibo | todos — separar vista de saldos de vacaciones */
  const [filtroModalidadVacaciones, setFiltroModalidadVacaciones] = useState('planilla');

  const empresaParaConstancia = useMemo(
    () => ({
      nombre: selectedCompany?.name ?? selectedCompany?.nombre_empresa ?? 'Empresa',
      ruc: selectedCompany?.ruc ?? '—',
      representante: (selectedCompany?.representante_legal || '').trim() || 'Recursos Humanos',
    }),
    [selectedCompany]
  );

  // Función para filtrar empleados activos en una fecha específica
  const filterEmployeesByDate = (selectedDate) => {
    if (!selectedDate || !employees || employees.length === 0) {
      console.log('🔍 Sin fecha seleccionada o sin empleados, devolviendo lista vacía');
      return [];
    }

    console.log('=== FILTRANDO EMPLEADOS POR FECHA (AUTORIZACIONES) ===');
    console.log('📅 Fecha seleccionada:', selectedDate);
    console.log('👥 Total empleados disponibles:', employees.length);

    const empleadosFiltrados = employees.filter(empleado => {
      console.log(`\n--- EVALUANDO EMPLEADO: ${empleado.nombre_completo} ---`);

      if (empleado.ultimo_historial?.motivo_cese === 'No se presentó a laborar') {
        return false;
      }

      if (empleadoActivoEnFechaPlameOInterno(empleado, selectedDate)) {
        return true;
      }

      if (!empleado.ultimo_historial) {
        const activoSinHistorial =
          empleado.estado === 'Activo' || empleado.esta_activo_actual === true;
        if (activoSinHistorial) {
          console.log(
            '⚠️ Sin historial laboral pero marcado como activo — INCLUIDO (p. ej. RH u historial pendiente)'
          );
          return true;
        }
        console.log('❌ Sin historial laboral - EXCLUIDO');
        return false;
      }

      return false;
    });

    console.log('✅ EMPLEADOS FILTRADOS FINALES (todos los activos en fecha):', empleadosFiltrados.length);
    empleadosFiltrados.forEach((emp, index) => {
      console.log(`${index + 1}. ${emp.nombre_completo} (${emp.estado}) - boleta_liquidada: ${emp.es_boleta_liquidada}`);
    });

    return empleadosFiltrados;
  };

  // Función para calcular días entre dos fechas
  const calculateDaysBetween = (startDate, endDate) => {
    if (!startDate || !endDate) return 0;
    
    const start = new Date(startDate);
    const end = new Date(endDate);
    const timeDifference = end.getTime() - start.getTime();
    const daysDifference = Math.ceil(timeDifference / (1000 * 3600 * 24)) + 1; // +1 para incluir ambos días
    
    return daysDifference > 0 ? daysDifference : 0;
  };

  // Función para obtener el inicio y fin de la semana
  const getWeekRange = (date) => {
    const currentDate = new Date(date);
    const startOfWeek = new Date(currentDate);
    const day = currentDate.getDay();
    const diff = currentDate.getDate() - day + (day === 0 ? -6 : 1); // ajuste para lunes como primer día
    startOfWeek.setDate(diff);
    startOfWeek.setHours(0, 0, 0, 0);
    
    const endOfWeek = new Date(startOfWeek);
    endOfWeek.setDate(startOfWeek.getDate() + 6);
    endOfWeek.setHours(23, 59, 59, 999);
    
    return { start: startOfWeek, end: endOfWeek };
  };

  // Función para obtener el inicio y fin del mes
  const getMonthRange = (date) => {
    const currentDate = new Date(date);
    const startOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
    const endOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
    endOfMonth.setHours(23, 59, 59, 999);
    
    return { start: startOfMonth, end: endOfMonth };
  };

  // Función para obtener el inicio y fin del año
  const getYearRange = (date) => {
    const currentDate = new Date(date);
    const startOfYear = new Date(currentDate.getFullYear(), 0, 1);
    const endOfYear = new Date(currentDate.getFullYear(), 11, 31);
    endOfYear.setHours(23, 59, 59, 999);
    
    return { start: startOfYear, end: endOfYear };
  };

  // Función para verificar si una autorización está en el rango de fechas
  const isAuthorizationInDateRange = (authorization) => {
    console.log('🔍 Evaluando autorización para filtro de fecha:', {
      authStartDate: authorization.startDate,
      filterType: filters.dateType,
      filterValue: filters.selectedDate
    });
    
    // Extraer solo la parte de fecha (YYYY-MM-DD) para evitar problemas de zona horaria
    const authDateStr = authorization.startDate.split('T')[0]; // Solo YYYY-MM-DD
    const authDate = new Date(authDateStr + 'T00:00:00'); // Agregar hora para evitar zona horaria
    
    switch (filters.dateType) {
      case 'day':
        const dayMatch = authDateStr === filters.selectedDate;
        console.log('📅 Filtro día:', authDateStr, '===', filters.selectedDate, '?', dayMatch);
        return dayMatch;
      
      case 'week':
        const selectedDate = new Date(filters.selectedDate + 'T00:00:00');
        const weekRange = getWeekRange(selectedDate);
        const weekMatch = authDate >= weekRange.start && authDate <= weekRange.end;
        console.log('📅 Filtro semana:', authDate, 'entre', weekRange.start, 'y', weekRange.end, '?', weekMatch);
        return weekMatch;
      
      case 'month':
        // Para filtro de mes, comparar año y mes directamente
        const [authYear, authMonth] = authDateStr.split('-');
        const [filterYear, filterMonth] = filters.selectedDate.split('-');
        const monthMatch = authYear === filterYear && authMonth === filterMonth;
        console.log('📅 Filtro mes:', `${authYear}-${authMonth}`, '===', `${filterYear}-${filterMonth}`, '?', monthMatch);
        return monthMatch;
      
      case 'year':
        // Para filtro de año, comparar solo el año
        const authYearOnly = authDateStr.split('-')[0];
        const filterYearOnly = filters.selectedDate.split('-')[0];
        const yearMatch = authYearOnly === filterYearOnly;
        console.log('📅 Filtro año:', authYearOnly, '===', filterYearOnly, '?', yearMatch);
        return yearMatch;
      
      default:
        console.log('📅 Sin filtro de fecha aplicado');
        return true;
    }
  };

  // Función para manejar cambios en filtros
  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
  };

  // Función para formatear fecha a dd/mm/yyyy con zona horaria de Lima
  const formatDateToDDMMYYYY = (dateString) => {
    if (!dateString) return 'N/A';
    
    try {
      // Si la fecha viene con formato ISO (con T y Z), usar método más directo
      if (dateString.includes('T')) {
        // Para fechas ISO, usar solo la parte de fecha para evitar problemas de zona horaria
        const datePart = dateString.split('T')[0]; // Obtener solo YYYY-MM-DD
        const [year, month, day] = datePart.split('-');
        return `${day}/${month}/${year}`;
      }
      
      // Para fechas simples YYYY-MM-DD
      if (dateString.includes('-') && dateString.length === 10) {
        const [year, month, day] = dateString.split('-');
        return `${day}/${month}/${year}`;
      }
      
      // Fallback para otros formatos usando zona horaria de Lima
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        throw new Error('Fecha inválida');
      }
      
      // Formatear usando zona horaria de Lima (América/Lima UTC-5)
      const limaDate = new Date(date.toLocaleString("en-US", {timeZone: "America/Lima"}));
      const day = limaDate.getDate().toString().padStart(2, '0');
      const month = (limaDate.getMonth() + 1).toString().padStart(2, '0');
      const year = limaDate.getFullYear();
      
      return `${day}/${month}/${year}`;
      
    } catch (error) {
      console.error('Error al formatear fecha:', dateString, error);
      return 'N/A';
    }
  };

  // Función para cargar empleados desde el backend
  const loadEmployees = async () => {
    try {
      console.log('=== CARGANDO EMPLEADOS PARA AUTORIZACIONES ===');
      console.log('🏢 Empresa ID:', selectedCompany?.id);
      
      const response = await tareoService.obtenerEmpleados(selectedCompany?.id);
      
      console.log('📋 Respuesta completa del backend:', response);
      console.log('✅ Success:', response.success);
      console.log('📊 Cantidad de empleados recibidos:', response.data?.length || 0);
      
      if (response.success) {
        console.log('👥 EMPLEADOS RECIBIDOS PARA AUTORIZACIONES:');
        response.data.forEach((empleado, index) => {
          console.log(`\n--- EMPLEADO ${index + 1} ---`);
          console.log('🆔 ID:', empleado.id);
          console.log('👤 Nombre:', empleado.nombre_completo);
          console.log('💼 Cargo:', empleado.cargo);
          console.log('🏗️ Proyecto:', empleado.proyecto);
          console.log('📊 Estado actual:', empleado.estado);
          console.log('✅ Está activo actualmente:', empleado.esta_activo_actual);
          
          if (empleado.ultimo_historial) {
            console.log('📅 ÚLTIMO HISTORIAL:');
            console.log('  - Fecha Ingreso:', empleado.ultimo_historial.fecha_ingreso);
            console.log('  - Fecha Cese:', empleado.ultimo_historial.fecha_cese || 'SIN CESE (ACTIVO)');
          } else {
            console.log('⚠️ SIN HISTORIAL LABORAL');
          }
        });
        
        setEmployees(response.data);
        console.log('💾 Empleados guardados en estado local');
        
      } else {
        console.error('❌ Error al cargar empleados:', response.message);
        setEmployees([]);
      }
    } catch (error) {
      console.error('💥 Error exception al cargar empleados:', error);
      setEmployees([]);
    } finally {
      setLoading(false);
    }
  };

  // useEffect para cargar datos al montar el componente y cuando cambie la empresa
  useEffect(() => {
    const loadData = async () => {
      if (selectedCompany?.id) {
      await loadEmployees();
      await loadAuthorizations();
      }
    };
    
    loadData();
  }, [selectedCompany]);

  // useEffect para cargar saldos iniciales cuando los empleados estén disponibles
  useEffect(() => {
    if (selectedCompany?.id && employees.length > 0) {
      loadInitialBalances();
    }
  }, [selectedCompany?.id, employees.length]);

  // Función para cargar autorizaciones desde el backend
  const loadAuthorizations = async () => {
    try {
      setLoadingAuthorizations(true);
      console.log('=== CARGANDO AUTORIZACIONES ===');
      console.log('🏢 Empresa ID:', selectedCompany?.id);
      
      const response = await autorizacionService.obtenerAutorizaciones({
        empresa_id: selectedCompany?.id
      });
      
      console.log('📋 Respuesta completa del backend:', response);
      console.log('✅ Success:', response.success);
      console.log('📊 Cantidad de autorizaciones recibidas:', response.data?.length || 0);
      
      if (response.success) {
        // Transformar datos del backend al formato del frontend
        const transformedAuthorizations = response.data.map(auth => ({
          id: auth.id,
          employeeId: auth.empleado_id,
          type: auth.tipo_autorizacion,
          subType: auth.sub_tipo,
          startDate: auth.fecha_inicio,
          endDate: auth.fecha_fin,
          hours: auth.horas,
          days: auth.dias,
          reason: auth.motivo,
          description: auth.descripcion,
          status: auth.estado,
          approvedBy: auth.usuario_aprueba?.name,
          rejectedBy: auth.usuario_rechaza?.name,
          createdAt: auth.fecha_solicitud,
          // Información del empleado
          employeeName: auth.empleado?.nombre_completo,
          employeePosition: auth.empleado?.cargo,
          employeeDocument: auth.empleado?.numero_documento
        }));
        
        console.log('✅ AUTORIZACIONES TRANSFORMADAS:');
        transformedAuthorizations.forEach((auth, index) => {
          console.log(`\n--- AUTORIZACIÓN ${index + 1} ---`);
          console.log('🆔 ID:', auth.id);
          console.log('👤 Empleado:', auth.employeeName);
          console.log('📋 Tipo:', auth.type);
          console.log('📅 Fecha:', auth.startDate);
          console.log('⚡ Estado:', auth.status);
        });
        
        setAuthorizations(transformedAuthorizations);
        console.log('💾 Autorizaciones guardadas en estado local');
        
      } else {
        console.error('❌ Error al cargar autorizaciones:', response.message);
        setAuthorizations([]);
      }
    } catch (error) {
      console.error('💥 Error exception al cargar autorizaciones:', error);
      setAuthorizations([]);
    } finally {
      setLoadingAuthorizations(false);
    }
  };

  // Función para calcular años trabajados SIN problemas de zona horaria
  const calcularAñosTrabajados = (fechaIngreso) => {
    if (!fechaIngreso) return 0;
    
    try {
      // Extraer solo la parte de fecha (YYYY-MM-DD)
      const fechaIngresoStr = fechaIngreso.includes('T') ? fechaIngreso.split('T')[0] : fechaIngreso;
      const fechaActualStr = new Date().toISOString().split('T')[0];
      
      console.log('📅 Calculando años trabajados:');
      console.log('  - Fecha ingreso:', fechaIngresoStr);
      console.log('  - Fecha actual:', fechaActualStr);
      
      // Construir fechas desde componentes separados
      const [añoIngreso, mesIngreso, diaIngreso] = fechaIngresoStr.split('-').map(Number);
      const [añoActual, mesActual, diaActual] = fechaActualStr.split('-').map(Number);
      
      // Calcular años exactos
      let años = añoActual - añoIngreso;
      
      // Ajustar si aún no ha llegado el "aniversario" este año
      if (mesActual < mesIngreso || (mesActual === mesIngreso && diaActual < diaIngreso)) {
        años--;
      }
      
      console.log(`  - Años completos trabajados: ${años}`);
      return Math.max(0, años);
      
    } catch (error) {
      console.error('Error al calcular años trabajados:', error);
      return 0;
    }
  };

  // Cálculo de períodos de vacaciones acumulados por años trabajados
  const getVacationPeriods = (employee) => {
    if (!employee) return null;
    
    // Obtener el código del régimen desde el empleado original
    const employeeData = employees.find(emp => emp.id === employee.id) || employee;
    const regimenCode = employeeData?.regimen_laboral;
    const esRH = esReciboHonorarios(employeeData);
    const regimenSoportado = ['RG', 'MYPP', 'MYPM'].includes(regimenCode);
    
    // Planilla: solo RG / MYPE. RH: también con control (default 30 días/año si no hay régimen)
    if (!regimenSoportado && !esRH) {
      return null;
    }

    const diasPorAño = regimenCode === 'RG'
      ? 30
      : (regimenSoportado ? 15 : 30);
    
    // Calcular años trabajados
    const fechaIngreso = employee.ultimo_historial?.fecha_ingreso || employeeData.ultimo_historial?.fecha_ingreso;
    if (!fechaIngreso) return null;
    
    const añosTrabajados = calcularAñosTrabajados(fechaIngreso);
    
    // Desde el primer día tiene derecho a días del primer año
    // + días adicionales por cada año completo trabajado
    const períodosDisponibles = añosTrabajados + 1;
    
    const periods = [];
    const fechaIngresoStr = fechaIngreso.includes('T') ? fechaIngreso.split('T')[0] : fechaIngreso;
    const [añoIngreso] = fechaIngresoStr.split('-').map(Number);
    
    for (let año = 1; año <= períodosDisponibles; año++) {
      const añoPeriodo = añoIngreso + año - 1;
      periods.push({
        year: año,
        calendarYear: añoPeriodo,
        days: diasPorAño,
        label: `Año ${año} (${añoPeriodo})`
      });
    }
    
    const totalDays = periods.length * diasPorAño;
    
    return { 
      periods, 
      totalDays, 
      isEligible: true,
      yearsWorked: añosTrabajados,
      daysPerYear: diasPorAño,
      esRH,
    };
  };

  // Función de compatibilidad (mantener para no romper código existente)
  const getVacationDays = (employee) => {
    const vacationData = getVacationPeriods(employee);
    return vacationData ? vacationData.totalDays : null;
  };

  // 🔧 Aplicar saldo inicial a los períodos de vacaciones (FIFO inverso)
  const applyInitialBalanceToVacationPeriods = (originalPeriods, initialBalance) => {
    if (initialBalance <= 0) {
      return [...originalPeriods]; // Sin ajuste si no hay saldo inicial
    }
    
    console.log(`🔧 Aplicando saldo inicial de ${initialBalance} días a períodos:`, originalPeriods);
    
    // Clonar períodos para no modificar el original
    const adjustedPeriods = originalPeriods.map(period => ({ ...period }));
    
    // Aplicar reducción FIFO (empezar por los períodos más antiguos)
    let diasPorRestar = initialBalance;
    
    for (let i = 0; i < adjustedPeriods.length && diasPorRestar > 0; i++) {
      const period = adjustedPeriods[i];
      const diasARestar = Math.min(diasPorRestar, period.days);
      
      period.days = Math.max(0, period.days - diasARestar);
      diasPorRestar -= diasARestar;
      
      console.log(`  - ${period.label}: Restados ${diasARestar} días, quedan ${period.days} días`);
    }
    
    // Filtrar períodos que quedaron en 0 días
    const periodosConDias = adjustedPeriods.filter(period => period.days > 0);
    
    console.log(`✅ Períodos finales después del ajuste:`, periodosConDias);
    
    return periodosConDias;
  };

  // Calcular consumo de vacaciones con sistema FIFO (First In, First Out)
  // Solo cuenta vacaciones del período laboral vigente (desde ultimo_historial.fecha_ingreso).
  // Así, días tomados en un ciclo previo (p. ej. cuando estaba en RH) no afectan el saldo de Planilla.
  const getVacationConsumption = (employeeId) => {
    const employee = employees.find(emp => Number(emp.id) === Number(employeeId));
    if (!employee) return { totalUsed: 0, consumptionByPeriod: [], remainingByPeriod: [], totalRemaining: 0 };

    const fechaInicioPeriodoRaw = employee.ultimo_historial?.fecha_ingreso;
    const fechaInicioPeriodo = fechaInicioPeriodoRaw
      ? (String(fechaInicioPeriodoRaw).includes('T')
          ? String(fechaInicioPeriodoRaw).split('T')[0]
          : String(fechaInicioPeriodoRaw).slice(0, 10))
      : null;

    const toDateOnly = (d) => {
      if (!d) return null;
      const s = String(d);
      return s.includes('T') ? s.split('T')[0] : s.slice(0, 10);
    };

    // Vacaciones aprobadas solo desde el ingreso del período actual
    const vacacionesAprobadas = authorizations
      .filter(auth => {
        if (Number(auth.employeeId) !== Number(employeeId)) return false;
        if (auth.type !== 'vacaciones' || auth.status !== 'aprobado') return false;
        if (!fechaInicioPeriodo) return true;
        const inicioVac = toDateOnly(auth.startDate);
        return inicioVac && inicioVac >= fechaInicioPeriodo;
      })
      .sort((a, b) => new Date(a.startDate) - new Date(b.startDate));
    
    console.log(`\n=== CONSUMO FIFO VACACIONES: Empleado ${employeeId} ===`);
    console.log('📅 Período laboral desde:', fechaInicioPeriodo || 'sin fecha (todas las vacaciones)');
    console.log('📋 Vacaciones aprobadas (solo período actual):');
    vacacionesAprobadas.forEach(vac => {
      console.log(`  - ${vac.startDate}: ${vac.days} días`);
    });
    
    const vacationData = getVacationPeriods(employee);
    if (!vacationData) {
      return { totalUsed: 0, consumptionByPeriod: [], remainingByPeriod: [], totalRemaining: 0 };
    }
    
    // 🔧 APLICAR SALDO INICIAL - Ajustar períodos según saldo inicial
    const initialBalance = getInitialBalance(employeeId);
    const adjustedPeriods = applyInitialBalanceToVacationPeriods(vacationData.periods, initialBalance);
    
    console.log(`⚙️ Saldo inicial a restar: ${initialBalance} días`);
    console.log(`📊 Períodos ajustados:`, adjustedPeriods);
    
    // Inicializar consumo por período con los períodos ajustados
    const consumptionByPeriod = adjustedPeriods.map(period => ({
      ...period,
      used: 0,
      remaining: period.days
    }));
    
    // Aplicar consumo FIFO
    let totalDiasUsados = vacacionesAprobadas.reduce((total, auth) => total + (auth.days || 0), 0);
    let diasPorConsumir = totalDiasUsados;
    
    console.log(`🏖️ Total días a consumir (período actual): ${totalDiasUsados}`);
    
    for (let i = 0; i < consumptionByPeriod.length && diasPorConsumir > 0; i++) {
      const period = consumptionByPeriod[i];
      const diasDisponibles = period.days;
      const diasAConsumir = Math.min(diasPorConsumir, diasDisponibles);
      
      period.used = diasAConsumir;
      period.remaining = diasDisponibles - diasAConsumir;
      diasPorConsumir -= diasAConsumir;
      
      console.log(`  - ${period.label}: Usado ${period.used}/${period.days} días, Restante: ${period.remaining}`);
    }
    
    const totalRestante = consumptionByPeriod.reduce((total, period) => total + period.remaining, 0);
    console.log(`✅ Total restante: ${totalRestante} días`);
    
    return {
      totalUsed: totalDiasUsados,
      totalRemaining: totalRestante,
      consumptionByPeriod,
      remainingByPeriod: consumptionByPeriod.filter(period => period.remaining > 0)
    };
  };

  // Función de compatibilidad para días utilizados (mantener para no romper código existente)
  const getUsedVacationDays = (employeeId) => {
    const consumption = getVacationConsumption(employeeId);
    return consumption.totalUsed;
  };

  // Generar datos de saldo de vacaciones
  const getVacationBalances = () => {
    // Filtrar empleados que NO tienen boleta liquidada (ya se les pagó las vacaciones)
    const empleadosConBoletaLiquidada = employees.filter(employee => employee.es_boleta_liquidada);
    
    // Filtrar empleados que están ACTIVOS (no cesados) y sin boleta liquidada
    const empleadosConVacacionesPendientes = employees.filter(employee => {
      // No tiene boleta liquidada
      const sinBoletaLiquidada = !employee.es_boleta_liquidada;
      
      // Está activo actualmente (sin fecha de cese)
      const estaActivo = employee.esta_activo_actual && 
                        employee.ultimo_historial && 
                        !employee.ultimo_historial.fecha_cese;
      
      return sinBoletaLiquidada && estaActivo;
    });
    
    // Empleados cesados (para estadísticas)
    const empleadosCesados = employees.filter(employee => 
      employee.ultimo_historial && employee.ultimo_historial.fecha_cese
    );
    
    console.log('=== FILTRO DE SALDOS DE VACACIONES ===');
    console.log('📊 Total empleados:', employees.length);
    console.log('💰 Empleados con boleta liquidada (excluidos):', empleadosConBoletaLiquidada.length);
    console.log('🚪 Empleados cesados (excluidos):', empleadosCesados.length);
    console.log('🏖️ Empleados activos con vacaciones pendientes:', empleadosConVacacionesPendientes.length);
    
    if (empleadosConBoletaLiquidada.length > 0) {
      console.log('💰 Lista de empleados con boleta liquidada:');
      empleadosConBoletaLiquidada.forEach(emp => {
        console.log(`   - ${emp.nombre_completo} (es_boleta_liquidada: ${emp.es_boleta_liquidada})`);
      });
    }
    
    if (empleadosCesados.length > 0) {
      console.log('🚪 Lista de empleados cesados:');
      empleadosCesados.forEach(emp => {
        console.log(`   - ${emp.nombre_completo} (fecha_cese: ${emp.ultimo_historial?.fecha_cese})`);
      });
    }
    
    return empleadosConVacacionesPendientes.map(employee => {
      const vacationData = getVacationPeriods(employee);
      const consumption = getVacationConsumption(employee.id);
      const esRH = esReciboHonorarios(employee);
      const modalidad = esRH ? 'recibo' : 'planilla';
      
             // Si no hay datos de vacaciones (régimen no soportado y no RH)
       if (!vacationData) {
         return {
           id: employee.id,
           name: employee.nombre_completo,
           position: employee.cargo,
           regime: 'Régimen no soportado',
           regimenCode: employee.regimen_laboral,
           modalidad,
           esRH,
           startDate: employee.ultimo_historial?.fecha_ingreso,
           yearsWorked: 0,
           totalDays: 0,
           usedDays: 0,
           pendingDays: 0,
           isSupported: false,
           isEligible: false,
           periods: [],
           detailedBalance: 'Régimen laboral no soportado'
         };
       }
      
      // Usar la nueva función para calcular días considerando saldo inicial
      const vacationCalculation = getTotalVacationDays(employee, vacationData);
      const totalDays = vacationCalculation.total;
      const usedDays = consumption.totalUsed;
      const pendingDays = totalDays - usedDays;
      
      // Generar descripción detallada del saldo
      const detailedBalance = consumption.remainingByPeriod.length > 0 
        ? consumption.remainingByPeriod.map(period => 
            `${period.label}: ${period.remaining} días`
          ).join(', ')
        : 'Sin saldo pendiente';
      
      // Mapear nombres de regímenes
      let regimeName;
      switch (employee.regimen_laboral) {
        case 'RG':
          regimeName = 'Régimen General';
          break;
        case 'MYPP':
          regimeName = 'MYPE Pequeña Empresa';
          break;
        case 'CONSTRUCCION':
          regimeName = 'Construcción Civil';
          break;
        case 'AGRARIO':
          regimeName = 'Régimen Agrario';
          break;
        case 'PESQUERO':
          regimeName = 'Régimen Pesquero';
          break;
        case 'DOMESTICO':
          regimeName = 'Régimen del Hogar';
          break;
        case 'EXPORTACION':
          regimeName = 'Régimen de Exportación No Tradicional';
          break;
        case 'MINERO':
          regimeName = 'Régimen Minero';
          break;
        case 'TEXTIL':
          regimeName = 'Régimen Textil y Confecciones';
          break;
        case 'JUVENIL':
          regimeName = 'Régimen Laboral Juvenil';
          break;
        case 'MYPM':
          regimeName = 'MYPE MicroEmpresa';
          break;
        default:
          regimeName = employee.regimen_laboral || 'No especificado';
      }

      if (esRH) {
        regimeName = ['RG', 'MYPP', 'MYPM'].includes(employee.regimen_laboral)
          ? `${regimeName} · RH`
          : 'Recibo por Honorarios (control)';
      }
      
      return {
        id: employee.id,
        name: employee.nombre_completo,
        position: employee.cargo,
        regime: regimeName,
        regimenCode: employee.regimen_laboral,
        modalidad,
        esRH,
        startDate: employee.ultimo_historial?.fecha_ingreso,
        yearsWorked: vacationData.yearsWorked,
        totalDays,
        usedDays,
        pendingDays,
        isSupported: true,
        isEligible: true,
        periods: consumption.consumptionByPeriod,
        detailedBalance,
        daysPerYear: vacationData.daysPerYear,
        // Información del saldo inicial
        initialBalance: vacationCalculation.initial,
        calculatedDays: vacationCalculation.calculated,
        adjustedDays: vacationCalculation.adjusted
      };
    });
  };

  // 🔧 Funciones para manejar saldos iniciales
  const handleEditBalance = (employeeId) => {
    setEditingBalance(employeeId);
    setTempBalance(initialBalances[employeeId] || '0');
  };

  const handleSaveBalance = async (employeeId) => {
    if (!selectedCompany?.id) {
      console.error('❌ No hay empresa seleccionada');
      return;
    }

    try {
      const days = parseInt(tempBalance) || 0;
      console.log(`💾 Guardando saldo inicial para empleado ${employeeId}: ${days} días`);
      
      const response = await configuracionInicialVacacionesService.guardarConfiguracion(
        selectedCompany.id,
        employeeId,
        {
          dias: days,
          observaciones: `Saldo inicial configurado desde el frontend - ${new Date().toLocaleString()}`
        }
      );

      if (response.success) {
        // Actualizar estado local
        setInitialBalances(prev => ({
          ...prev,
          [employeeId]: days
        }));
        
        setEditingBalance(null);
        setTempBalance('');
        console.log(`✅ Saldo inicial guardado exitosamente: ${days} días`);
      } else {
        console.error('❌ Error al guardar saldo inicial:', response.message);
        alert('Error al guardar el saldo inicial. Por favor, intenta nuevamente.');
      }
    } catch (error) {
      console.error('❌ Error al guardar saldo inicial:', error);
      alert('Error al guardar el saldo inicial. Por favor, intenta nuevamente.');
    }
  };

  const handleCancelEdit = () => {
    setEditingBalance(null);
    setTempBalance('');
  };

  // 🔄 Cargar saldos iniciales desde el backend
  const loadInitialBalances = async () => {
    if (!selectedCompany?.id || !employees.length) return;

    try {
      setLoadingInitialBalances(true);
      console.log('🔄 Cargando saldos iniciales desde el backend...');
      
      const empleadosIds = employees.map(emp => emp.id);
      const response = await configuracionInicialVacacionesService.obtenerConfiguracionesMultiples(
        selectedCompany.id, 
        empleadosIds
      );
      
      if (response.success) {
        setInitialBalances(response.data);
        console.log('✅ Saldos iniciales cargados:', response.data);
      } else {
        console.error('❌ Error al cargar saldos iniciales:', response.message);
      }
    } catch (error) {
      console.error('❌ Error al cargar saldos iniciales:', error);
    } finally {
      setLoadingInitialBalances(false);
    }
  };

  const getInitialBalance = (employeeId) => {
    return initialBalances[employeeId] || 0;
  };

  // 📊 Función modificada para calcular días correspondientes considerando saldo inicial
  const getTotalVacationDays = (employee, vacationData) => {
    const calculatedDays = vacationData ? vacationData.totalDays : 0;
    const initialDays = getInitialBalance(employee.id);
    
    // Los días iniciales se RESTAN de los calculados (para compensar antigüedad excesiva)
    const adjustedDays = Math.max(0, calculatedDays - initialDays);
    
    console.log(`📊 Empleado ${employee.nombre_completo}:`);
    console.log(`   Días calculados: ${calculatedDays}`);
    console.log(`   Saldo inicial (a restar): ${initialDays}`);
    console.log(`   Días finales (ajustados): ${adjustedDays}`);
    
    return {
      calculated: calculatedDays,
      initial: initialDays,
      adjusted: adjustedDays,
      total: adjustedDays // Solo los días ajustados (reducidos por saldo inicial)
    };
  };

  // Filtrar saldos después de definir getInitialBalance / getTotalVacationDays
  const vacationBalancesFiltrados = useMemo(() => {
    const todos = getVacationBalances();
    if (filtroModalidadVacaciones === 'recibo') {
      return todos.filter((b) => b.modalidad === 'recibo');
    }
    if (filtroModalidadVacaciones === 'planilla') {
      return todos.filter((b) => b.modalidad === 'planilla');
    }
    return todos;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtroModalidadVacaciones, employees, authorizations, initialBalances]);

  const conteoModalidadVacaciones = useMemo(() => {
    const todos = getVacationBalances();
    return {
      planilla: todos.filter((b) => b.modalidad === 'planilla').length,
      recibo: todos.filter((b) => b.modalidad === 'recibo').length,
      todos: todos.length,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employees, authorizations, initialBalances]);

  const tabs = [
    { id: 'horas-extra', name: 'Horas Extra', icon: '⏰', color: 'text-blue-600' },
    { id: 'permisos', name: 'Permisos', icon: '📋', color: 'text-green-600' },
    { id: 'suspensiones', name: 'Suspensiones', icon: '⛔', color: 'text-amber-600' },
    { id: 'descansos-medicos', name: 'Descansos Médicos', icon: '🏥', color: 'text-red-600' },
    { id: 'subsidios-enfermedad', name: 'Subsidios por Enfermedad', icon: '💊', color: 'text-orange-600' },
    { id: 'licencias-goce', name: 'Licencia con Goce de Haber', icon: '🧾', color: 'text-indigo-600' },
    { id: 'vacaciones', name: 'Vacaciones', icon: '🏖️', color: 'text-purple-600' }
  ];

  // Tipos de horas extra con sus respectivos porcentajes
  const tiposHorasExtra = [
    { 
      value: 'horas-extra-25', 
      label: 'Horas Extra 25%', 
      description: 'Primeras 2 horas (días laborables)',
      porcentaje: 25,
      codigo: 'HE25'
    },
    { 
      value: 'horas-extra-35', 
      label: 'Horas Extra 35%', 
      description: 'Después de 2 horas o domingos/feriados',
      porcentaje: 35,
      codigo: 'HE35'
    }
  ];

  const vacationSubTabs = [
    { id: 'solicitudes', name: 'Solicitudes', icon: '📋' },
    { id: 'saldos', name: 'Saldos Pendientes', icon: '📊' }
  ];

  const getEmployeeName = (employeeId) => {
    const employee = employees.find(emp => Number(emp.id) === Number(employeeId));
    if (employee) return employee.nombre_completo;
    const authRef = authorizations.find((a) => Number(a.employeeId) === Number(employeeId));
    return authRef?.employeeName || 'N/A';
  };

  const getEmployeeInfo = (employeeId) => {
    console.log('🔍 Buscando empleado con ID:', employeeId);
    console.log('📋 Empleados disponibles:', employees.map(emp => ({ id: emp.id, nombre: emp.nombre_completo })));
    
    const employee = employees.find(emp => Number(emp.id) === Number(employeeId));
    
    if (!employee) {
      const authRef = authorizations.find((a) => Number(a.employeeId) === Number(employeeId));
      if (authRef?.employeeName) {
        console.log('⚠️ Empleado no está en lista de empleados, usando nombre desde autorización');
        return {
          id: Number(employeeId),
          name: authRef.employeeName,
          position: authRef.employeePosition || 'Sin cargo',
          document: authRef.employeeDocument || '—',
          department: 'Sin proyecto',
          startDate: null,
          endDate: null,
          isActive: false,
          regime: 'general'
        };
      }
      console.log('❌ Empleado no encontrado con ID:', employeeId);
      return null;
    }
    
    console.log('✅ Empleado encontrado:', employee.nombre_completo);
    
    // Transformar estructura del backend a formato esperado por el frontend
    return {
      id: employee.id,
      name: employee.nombre_completo,
      position: employee.cargo,
      document: employee.numero_documento || '—',
      department: employee.proyecto || 'Sin proyecto',
      startDate: employee.ultimo_historial?.fecha_ingreso,
      endDate: employee.ultimo_historial?.fecha_cese,
      isActive: employee.esta_activo_actual,
      regime: employee.regimen_laboral || 'general'
    };
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pendiente: { class: 'bg-yellow-100 text-yellow-800', text: 'Pendiente' },
      aprobado: { class: 'bg-green-100 text-green-800', text: 'Aprobado' },
      rechazado: { class: 'bg-red-100 text-red-800', text: 'Rechazado' }
    };
    return statusConfig[status] || statusConfig.pendiente;
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'horas-extra': return '⏰';
      case 'permiso': return '📋';
      case 'suspension': return '⛔';
      case 'descanso-medico': return '🏥';
      case 'subsidio-por-enfermedad': return '💊';
      case 'licencia-con-goce-de-haber': return '🧾';
      case 'vacaciones': return '🏖️';
      default: return '📄';
    }
  };

  const getTypeLabel = (type, subType = null) => {
    switch (type) {
      case 'horas-extra': 
        if (subType) {
          const tipoHE = tiposHorasExtra.find(t => t.value === subType);
          return tipoHE ? tipoHE.label : 'Horas Extra';
        }
        return 'Horas Extra';
      case 'permiso': return 'Permiso';
      case 'suspension': return 'Suspensión';
      case 'descanso-medico': return 'Descanso Médico';
      case 'subsidio-por-enfermedad': return 'Subsidio por Enfermedad';
      case 'licencia-con-goce-de-haber': return 'Licencia con Goce de Haber';
      case 'vacaciones': return 'Vacaciones';
      default: return 'Desconocido';
    }
  };

  // Función para obtener información adicional del tipo de hora extra
  const getExtraHourInfo = (type, subType = null) => {
    if (type === 'horas-extra' && subType) {
      const tipoHE = tiposHorasExtra.find(t => t.value === subType);
      return tipoHE ? {
        porcentaje: tipoHE.porcentaje,
        descripcion: tipoHE.descripcion
      } : null;
    }
    return null;
  };

  const filterAuthorizations = () => {
    const typeMap = {
      'horas-extra': 'horas-extra',
      'permisos': 'permiso',
      'suspensiones': 'suspension',
      'descansos-medicos': 'descanso-medico',
      'subsidios-enfermedad': 'subsidio-por-enfermedad',
      'licencias-goce': 'licencia-con-goce-de-haber',
      'vacaciones': 'vacaciones'
    };
    
    return authorizations.filter(auth => {
      // Filtro por tipo (tab activo)
      const typeMatch = auth.type === typeMap[activeTab];
      
      // Filtro por fecha
      const dateMatch = isAuthorizationInDateRange(auth);
      
      // Filtro por nombre de empleado
      const employee = getEmployeeInfo(auth.employeeId);
      const nameMatch = filters.employeeName === '' || 
                        (employee?.name?.toLowerCase().includes(filters.employeeName.toLowerCase()) || false);
      
      // Filtro por estado
      const statusMatch = filters.status === 'all' || auth.status === filters.status;
      
      return typeMatch && dateMatch && nameMatch && statusMatch;
    });
  };

  const getTabStats = () => {
    const filtered = filterAuthorizations();
    return {
      total: filtered.length,
      pendientes: filtered.filter(a => a.status === 'pendiente').length,
      aprobados: filtered.filter(a => a.status === 'aprobado').length,
      rechazados: filtered.filter(a => a.status === 'rechazado').length
    };
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewAuthorization(prev => {
      const updated = {
      ...prev,
      [name]: value
      };

      // Calcular días automáticamente para permisos, vacaciones, descansos médicos, subsidios y licencias con goce
      if ((name === 'startDate' || name === 'endDate') && 
          (updated.type === 'permiso' || updated.type === 'suspension' || updated.type === 'vacaciones' || updated.type === 'descanso-medico' || updated.type === 'subsidio-por-enfermedad' || updated.type === 'licencia-con-goce-de-haber')) {
        const days = calculateDaysBetween(updated.startDate, updated.endDate);
        updated.days = days > 0 ? days.toString() : '';
      }

      // Limpiar empleado si cambia la fecha de inicio (para filtrar empleados activos)
      if (name === 'startDate') {
        updated.employeeId = '';
      }

      return updated;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!newAuthorization.employeeId || !newAuthorization.startDate || !newAuthorization.reason) {
      alert('Por favor completa todos los campos requeridos');
      return;
    }

    if (submitting) return;

    try {
      setSubmitting(true);
      console.log('=== CREANDO NUEVA AUTORIZACIÓN ===');

      // Preparar datos para el backend
      const authData = {
        empresa_id: selectedCompany?.id,
        empleado_id: parseInt(newAuthorization.employeeId),
        tipo_autorizacion: newAuthorization.type, // Usar directamente el tipo seleccionado en el dropdown
        sub_tipo: newAuthorization.subType,
        fecha_inicio: newAuthorization.startDate,
        fecha_fin: newAuthorization.endDate || newAuthorization.startDate,
        horas: newAuthorization.hours ? parseFloat(newAuthorization.hours) : null,
        dias: newAuthorization.days ? parseInt(newAuthorization.days) : null,
        motivo: newAuthorization.reason,
        descripcion: newAuthorization.description || null,
        dias_reposo_medico: newAuthorization.diasReposoMedico || []
      };

      console.log('📋 Datos a enviar:', authData);
      
      // Log específico para días de reposo médico
      if (newAuthorization.type === 'descanso-medico' && newAuthorization.diasReposoMedico?.length > 0) {
        console.log('🛏️ Días de reposo médico seleccionados:', newAuthorization.diasReposoMedico);
      }

      const response = await autorizacionService.crearAutorizacion(authData);
      
      console.log('✅ Respuesta del backend:', response);

      if (response.success) {
        console.log('🎉 Autorización creada exitosamente');
        alert('Autorización creada exitosamente');
        
        // Recargar autorizaciones
        await loadAuthorizations();
        
        // Cerrar modal y limpiar formulario
        resetModal();
      } else {
        console.error('❌ Error al crear autorización:', response.message);
        alert(`Error al crear autorización: ${response.message}`);
      }
    } catch (error) {
      console.error('💥 Error exception al crear autorización:', error);
      console.error('📋 Respuesta completa del error:', error.response?.data);
      
      // Extraer el mensaje específico del backend si está disponible
      const errorMessage = error.response?.data?.message || error.message || 'Error desconocido';
      alert(`Error al crear autorización: ${errorMessage}`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleApprove = async (id) => {
    try {
      console.log(`=== APROBANDO AUTORIZACIÓN ${id} ===`);
      
      const response = await autorizacionService.aprobarAutorizacion(id, 'Aprobado desde el sistema');
      
      console.log('✅ Respuesta del backend:', response);

      if (response.success) {
        console.log('🎉 Autorización aprobada exitosamente');
        alert('Autorización aprobada exitosamente');
        
        // Recargar autorizaciones para reflejar cambios
        await loadAuthorizations();
      } else {
        console.error('❌ Error al aprobar autorización:', response.message);
        alert(`Error al aprobar autorización: ${response.message}`);
      }
    } catch (error) {
      console.error('💥 Error exception al aprobar autorización:', error);
      console.error('📋 Respuesta completa del error:', error.response?.data);
      
      // Extraer el mensaje específico del backend si está disponible
      const errorMessage = error.response?.data?.message || error.message || 'Error desconocido';
      alert(`Error al aprobar autorización: ${errorMessage}`);
    }
  };

  const handleReject = async (id) => {
    try {
      console.log(`=== RECHAZANDO AUTORIZACIÓN ${id} ===`);
      
      const response = await autorizacionService.rechazarAutorizacion(id, 'Rechazado desde el sistema');
      
      console.log('✅ Respuesta del backend:', response);

      if (response.success) {
        console.log('🎉 Autorización rechazada exitosamente');
        alert('Autorización rechazada exitosamente');
        
        // Recargar autorizaciones para reflejar cambios
        await loadAuthorizations();
      } else {
        console.error('❌ Error al rechazar autorización:', response.message);
        alert(`Error al rechazar autorización: ${response.message}`);
      }
    } catch (error) {
      console.error('💥 Error exception al rechazar autorización:', error);
      console.error('📋 Respuesta completa del error:', error.response?.data);
      
      // Extraer el mensaje específico del backend si está disponible
      const errorMessage = error.response?.data?.message || error.message || 'Error desconocido';
      alert(`Error al rechazar autorización: ${errorMessage}`);
    }
  };

  const handlePending = async (id) => {
    try {
      console.log(`=== MARCANDO COMO PENDIENTE AUTORIZACIÓN ${id} ===`);
      
      const response = await autorizacionService.marcarPendiente(id, 'Marcado como pendiente desde el sistema');
      
      console.log('✅ Respuesta del backend:', response);

      if (response.success) {
        console.log('🎉 Autorización marcada como pendiente exitosamente');
        alert('Autorización marcada como pendiente exitosamente');
        
        // Recargar autorizaciones para reflejar cambios
        await loadAuthorizations();
      } else {
        console.error('❌ Error al marcar como pendiente:', response.message);
        alert(`Error al marcar como pendiente: ${response.message}`);
      }
    } catch (error) {
      console.error('💥 Error exception al marcar como pendiente:', error);
      console.error('📋 Respuesta completa del error:', error.response?.data);
      
      // Extraer el mensaje específico del backend si está disponible
      const errorMessage = error.response?.data?.message || error.message || 'Error desconocido';
      alert(`Error al marcar como pendiente: ${errorMessage}`);
    }
  };

  // Función para generar y descargar constancia (PDF) - Unificada para todos los tipos
  const handleDownloadConstancia = async (auth) => {
    try {
      console.log('🔄 Generando constancia para:', auth);

      if (!selectedCompany?.id) {
        alert('Seleccione una empresa para generar la constancia.');
        return;
      }
      
      const employee = getEmployeeInfo(auth.employeeId);
      if (!employee) {
        console.error('❌ No se encontró información del empleado');
        alert('No se encontró información del empleado');
        return;
      }

      // Verificar que la autorización esté aprobada
      if (auth.status !== 'aprobado') {
        alert('Solo se pueden descargar constancias de autorizaciones aprobadas');
        return;
      }

      const empresa = empresaParaConstancia;
      const firmaBase64 = await obtenerFirmaEmpresaBase64(selectedCompany.id);
      console.log('📋 Generando PDF...', { empresa, employee, tieneFirma: !!firmaBase64 });
      
      // Crear nuevo documento PDF
      const doc = new jsPDF();
      
      // Configuración de colores y fuentes
      const primaryColor = [37, 99, 235]; // Azul
      const grayColor = [55, 65, 81]; // Gris oscuro
      const lightGrayColor = [243, 244, 246]; // Gris claro
      
      // Obtener fecha actual en Lima
      const fechaActual = new Date().toLocaleDateString('es-PE', {
        timeZone: 'America/Lima',
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
      
      // Información de la autorización según el tipo
      const fechaInicio = formatDateToDDMMYYYY(auth.startDate);
      const fechaFin = auth.endDate ? formatDateToDDMMYYYY(auth.endDate) : fechaInicio;
      
      // Configuración específica por tipo
      let titulo, descripcionAccion, datosEspecificos;
      
      switch (auth.type) {
        case 'horas-extra':
          titulo = 'CONSTANCIA DE HORAS EXTRA';
          descripcionAccion = 'Ha sido autorizado(a) para realizar horas extra con las siguientes características:';
          const porcentaje = auth.subType === 'horas-extra-25' ? '25%' : '35%';
          const tipoDescripcion = auth.subType === 'horas-extra-25' 
            ? 'Primeras 2 horas (días laborables)' 
            : 'Después de 2 horas o domingos/feriados';
          datosEspecificos = [
            { label: 'Fecha:', valor: fechaInicio },
            { label: 'Horas autorizadas:', valor: `${auth.hours} horas` },
            { label: 'Tipo de horas extra:', valor: `${porcentaje} de recargo` },
            { label: 'Descripción:', valor: tipoDescripcion }
          ];
          break;
          
        case 'permiso':
          titulo = 'CONSTANCIA DE PERMISO';
          descripcionAccion = 'Ha sido autorizado(a) para ausentarse por permiso con las siguientes características:';
          datosEspecificos = [
            { label: 'Fecha de inicio:', valor: fechaInicio },
            { label: 'Fecha de fin:', valor: fechaFin },
            { label: 'Días autorizados:', valor: `${auth.days} día(s)` },
            { label: 'Tipo:', valor: 'Permiso laboral' }
          ];
          break;

        case 'suspension':
          titulo = 'CONSTANCIA DE SUSPENSIÓN';
          descripcionAccion = 'Ha sido autorizado(a) para suspensión con las siguientes características:';
          datosEspecificos = [
            { label: 'Fecha de inicio:', valor: fechaInicio },
            { label: 'Fecha de fin:', valor: fechaFin },
            { label: 'Días autorizados:', valor: `${auth.days} día(s)` },
            { label: 'Tipo:', valor: 'Suspensión' }
          ];
          break;
          
        case 'descanso-medico':
          titulo = 'CONSTANCIA DE DESCANSO MÉDICO';
          descripcionAccion = 'Ha sido autorizado(a) para descanso médico con las siguientes características:';
          datosEspecificos = [
            { label: 'Fecha de inicio:', valor: fechaInicio },
            { label: 'Fecha de fin:', valor: fechaFin },
            { label: 'Días autorizados:', valor: `${auth.days} día(s)` },
            { label: 'Tipo:', valor: 'Descanso médico' }
          ];
          break;
          
        case 'subsidio-por-enfermedad':
          titulo = 'CONSTANCIA DE SUBSIDIO POR ENFERMEDAD';
          descripcionAccion = 'Ha sido autorizado(a) para subsidio por enfermedad con las siguientes características:';
          datosEspecificos = [
            { label: 'Fecha de inicio:', valor: fechaInicio },
            { label: 'Fecha de fin:', valor: fechaFin },
            { label: 'Días autorizados:', valor: `${auth.days} día(s)` },
            { label: 'Tipo:', valor: 'Subsidio por enfermedad' }
          ];
          break;

        case 'licencia-con-goce-de-haber':
          titulo = 'CONSTANCIA DE LICENCIA CON GOCE DE HABER';
          descripcionAccion = 'Ha sido autorizado(a) para licencia con goce de haber con las siguientes características:';
          datosEspecificos = [
            { label: 'Fecha de inicio:', valor: fechaInicio },
            { label: 'Fecha de fin:', valor: fechaFin },
            { label: 'Días autorizados:', valor: `${auth.days} día(s)` },
            { label: 'Tipo:', valor: 'Licencia con goce de haber' }
          ];
          break;
          
        case 'vacaciones':
          titulo = 'CONSTANCIA DE VACACIONES';
          descripcionAccion = 'Ha sido autorizado(a) para el goce de vacaciones con las siguientes características:';
          datosEspecificos = [
            { label: 'Fecha de inicio:', valor: fechaInicio },
            { label: 'Fecha de fin:', valor: fechaFin },
            { label: 'Días de vacaciones:', valor: `${auth.days} día(s)` },
            { label: 'Tipo:', valor: 'Vacaciones anuales' }
          ];
          break;
          
        default:
          titulo = 'CONSTANCIA DE AUTORIZACIÓN';
          descripcionAccion = 'Ha sido autorizado(a) con las siguientes características:';
          datosEspecificos = [
            { label: 'Fecha:', valor: fechaInicio },
            { label: 'Tipo:', valor: getTypeLabel(auth.type, auth.subType) }
          ];
      }

      // ENCABEZADO — datos de la empresa seleccionada
      doc.setFontSize(18);
      doc.setTextColor(...primaryColor);
      doc.setFont('helvetica', 'bold');
      const nombreEmpresaLines = doc.splitTextToSize(empresa.nombre, 170);
      doc.text(nombreEmpresaLines, 105, 25, { align: 'center' });
      
      doc.setFontSize(12);
      doc.setTextColor(...grayColor);
      doc.setFont('helvetica', 'normal');
      const yRuc = 25 + nombreEmpresaLines.length * 7;
      doc.text(`RUC: ${empresa.ruc}`, 105, yRuc, { align: 'center' });

      // TÍTULO
      const yTitulo = yRuc + 14;
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...grayColor);
      doc.text(titulo, 105, yTitulo, { align: 'center' });
      
      // Línea decorativa
      doc.setDrawColor(...primaryColor);
      doc.setLineWidth(0.5);
      doc.line(40, yTitulo + 5, 170, yTitulo + 5);

      // CONTENIDO PRINCIPAL
      let yPosition = yTitulo + 20;
      
      doc.setFontSize(11);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...grayColor);
      doc.text('Por medio de la presente, hacemos constar que el(la) trabajador(a):', 20, yPosition);
      
      yPosition += 15;

      // DATOS DEL TRABAJADOR (Recuadro)
      const alturaRecuadroEmpleado = 35;
      doc.setFillColor(...lightGrayColor);
      doc.rect(20, yPosition, 170, alturaRecuadroEmpleado, 'F');
      doc.setDrawColor(...grayColor);
      doc.rect(20, yPosition, 170, alturaRecuadroEmpleado, 'S');
      
      yPosition += 10;
      doc.setFont('helvetica', 'bold');
      doc.text('Nombre:', 25, yPosition);
      doc.setFont('helvetica', 'normal');
      const nombreLines = doc.splitTextToSize(employee.name || '—', 115);
      doc.text(nombreLines, 50, yPosition);
      
      yPosition += Math.max(8, nombreLines.length * 5);
      doc.setFont('helvetica', 'bold');
      doc.text('DNI:', 25, yPosition);
      doc.setFont('helvetica', 'normal');
      doc.text(employee.document || '—', 50, yPosition);
      
      yPosition += 8;
      doc.setFont('helvetica', 'bold');
      doc.text('Cargo:', 25, yPosition);
      doc.setFont('helvetica', 'normal');
      const cargoLines = doc.splitTextToSize(employee.position || 'N/A', 115);
      doc.text(cargoLines, 50, yPosition);
      
      yPosition += Math.max(12, cargoLines.length * 5);

      doc.setFont('helvetica', 'normal');
      doc.text(descripcionAccion, 20, yPosition);
      
      yPosition += 15;

      // DATOS DE LA AUTORIZACIÓN (Recuadro) - Dinámico según el tipo
      const alturaRecuadro = Math.max(35, datosEspecificos.length * 8 + 15);
      doc.setFillColor(...lightGrayColor);
      doc.rect(20, yPosition, 170, alturaRecuadro, 'F');
      doc.setDrawColor(...grayColor);
      doc.rect(20, yPosition, 170, alturaRecuadro, 'S');
      
      yPosition += 10;
      
      // Renderizar datos específicos
      datosEspecificos.forEach((dato) => {
        doc.setFont('helvetica', 'bold');
        doc.text(dato.label, 25, yPosition);
        doc.setFont('helvetica', 'normal');
        doc.text(dato.valor, 25 + doc.getTextWidth(dato.label) + 5, yPosition);
        yPosition += 8;
      });
      
      // Agregar motivo al final
      doc.setFont('helvetica', 'bold');
      doc.text('Motivo:', 25, yPosition);
      doc.setFont('helvetica', 'normal');
      // Dividir el motivo en líneas si es muy largo
      const motivoLines = doc.splitTextToSize(auth.reason, 130);
      doc.text(motivoLines, 25 + doc.getTextWidth('Motivo:') + 5, yPosition);
      
      if (motivoLines.length > 1) {
        yPosition += (motivoLines.length - 1) * 5;
      }
      
      yPosition += 20;

      doc.setFont('helvetica', 'normal');
      doc.text('Se expide la presente constancia para los fines que el interesado considere conveniente.', 20, yPosition);
      
      // FOOTER — fecha, firma del empleador (si está configurada) y representante
      yPosition = 250;
      doc.text(fechaActual, 150, yPosition);

      const firmaAncho = 50;
      const firmaAlto = 20;
      const firmaX = 150 - firmaAncho / 2;
      let yLineaFirma = 268;

      if (firmaBase64) {
        const yFirma = 238;
        doc.addImage(
          firmaBase64,
          formatoImagenFirma(firmaBase64),
          firmaX,
          yFirma,
          firmaAncho,
          firmaAlto
        );
        yLineaFirma = yFirma + firmaAlto + 4;
      }

      doc.line(120, yLineaFirma, 180, yLineaFirma);
      yPosition = yLineaFirma + 7;
      doc.setFont('helvetica', 'bold');
      doc.text(empresa.representante, 150, yPosition, { align: 'center' });
      yPosition += 5;
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);
      const pieEmpresaLines = doc.splitTextToSize(empresa.nombre, 55);
      doc.text(pieEmpresaLines, 150, yPosition, { align: 'center' });

      // Generar nombre del archivo según el tipo
      const fechaFormateada = formatDateToDDMMYYYY(auth.startDate).replace(/\//g, '-');
      let tipoArchivo;
      
      switch (auth.type) {
        case 'horas-extra':
          const tipoHora = auth.subType === 'horas-extra-25' ? '25' : '35';
          tipoArchivo = `HorasExtra_${tipoHora}pct`;
          break;
        case 'permiso':
          tipoArchivo = 'Permiso';
          break;
        case 'suspension':
          tipoArchivo = 'Suspension';
          break;
        case 'descanso-medico':
          tipoArchivo = 'DescansoMedico';
          break;
        case 'subsidio-por-enfermedad':
          tipoArchivo = 'SubsidioEnfermedad';
          break;
        case 'licencia-con-goce-de-haber':
          tipoArchivo = 'LicenciaConGoceHaber';
          break;
        case 'vacaciones':
          tipoArchivo = 'Vacaciones';
          break;
        default:
          tipoArchivo = 'Autorizacion';
      }
      
      const nombreArchivo = `Constancia_${tipoArchivo}_${employee.name.replace(/\s+/g, '_')}_${fechaFormateada}.pdf`;
      
      // Descargar el PDF
      doc.save(nombreArchivo);
      
      console.log('✅ Constancia generada y descargada exitosamente:', nombreArchivo);
      
    } catch (error) {
      console.error('💥 Error al generar constancia:', error);
      alert(`Error al generar constancia: ${error.message || 'Error desconocido'}`);
    }
  };

  // Función para limpiar el modal
  const resetModal = () => {
    setNewAuthorization({
      employeeId: '',
      type: 'horas-extra',
      subType: 'horas-extra-25',
      startDate: '',
      endDate: '',
      hours: '',
      days: '',
      reason: '',
      description: '',
      status: 'pendiente',
      diasReposoMedico: []
    });
    setShowModal(false);
  };

  const stats = getTabStats();

  // Función para generar array de días entre dos fechas (usando strings para evitar problemas de zona horaria)
  const getDaysInRange = (startDate, endDate) => {
    if (!startDate || !endDate) return [];
    
    console.log('🗓️ getDaysInRange - Generando días entre fechas:');
    console.log('📅 Fecha inicio (string):', startDate);
    console.log('📅 Fecha fin (string):', endDate);
    
    // Trabajar directamente con strings de fecha para evitar problemas de zona horaria
    const startParts = startDate.split('-');
    const endParts = endDate.split('-');
    
    const startYear = parseInt(startParts[0]);
    const startMonth = parseInt(startParts[1]);
    const startDay = parseInt(startParts[2]);
    
    const endYear = parseInt(endParts[0]);
    const endMonth = parseInt(endParts[1]);
    const endDay = parseInt(endParts[2]);
    
    console.log('🔢 Fecha inicio parseada:', { startYear, startMonth, startDay });
    console.log('🔢 Fecha fin parseada:', { endYear, endMonth, endDay });
    
    const days = [];
    
    // Crear fecha inicial usando componentes exactos (mediodía para evitar DST)
    let currentDate = new Date(startYear, startMonth - 1, startDay, 12, 0, 0);
    const finalDate = new Date(endYear, endMonth - 1, endDay, 12, 0, 0);
    
    console.log('🕐 Fecha inicial creada:', currentDate);
    console.log('🕐 Fecha final creada:', finalDate);
    
    while (currentDate <= finalDate) {
      // Extraer componentes de fecha directamente
      const year = currentDate.getFullYear();
      const month = String(currentDate.getMonth() + 1).padStart(2, '0');
      const day = String(currentDate.getDate()).padStart(2, '0');
      const dateString = `${year}-${month}-${day}`;
      
      // Obtener nombre del día en español
      const dayName = currentDate.toLocaleDateString('es-ES', { 
        weekday: 'short',
        timeZone: 'America/Lima' // Forzar zona horaria de Lima
      });
      
      console.log(`📆 Agregando día: ${dateString} (${dayName})`);
      
      days.push({
        date: dateString,
        dayName: dayName,
        dayNumber: currentDate.getDate()
      });
      
      // Avanzar al siguiente día usando setDate para evitar problemas de mes/año
      currentDate.setDate(currentDate.getDate() + 1);
    }
    
    console.log('✅ Días generados:', days.length);
    console.log('📋 Lista completa de días:', days);
    
    return days;
  };

  // Función para toggle day de reposo médico
  const toggleReposoDay = (dateString) => {
    setNewAuthorization(prev => ({
      ...prev,
      diasReposoMedico: prev.diasReposoMedico.includes(dateString)
        ? prev.diasReposoMedico.filter(d => d !== dateString)
        : [...prev.diasReposoMedico, dateString]
    }));
  };

  // Si no hay empresa seleccionada, mostrar mensaje
  if (!selectedCompany) {
    return (
      <div className="space-y-6">
        <div className="flex flex-col items-center justify-center h-64">
          <div className="text-gray-400 mb-4">
            <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No hay empresa seleccionada</h3>
          <p className="text-gray-500 text-center">
            Selecciona una empresa para gestionar las autorizaciones.
          </p>
        </div>
      </div>
    );
  }

  // Mostrar loading mientras se cargan los datos iniciales
  if (loading) {
  return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Cargando datos...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3 lg:space-y-4">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
        <div>
          <h1 className="text-xl lg:text-2xl font-bold text-gray-900">Autorizaciones</h1>
          <p className="text-gray-600">Gestión de horas extra, permisos, descansos médicos y vacaciones</p>
        </div>
        <button
          onClick={() => setShowModal(true)}
          className="flex items-center justify-center space-x-2 px-4 py-2 bg-gradient-to-r from-teal-500 to-teal-600 text-white rounded-lg font-medium hover:from-teal-600 hover:to-teal-700 transition-all duration-200"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
          </svg>
          <span>Nueva Autorización</span>
        </button>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-4">
        <div className="bg-white rounded-xl p-3 lg:p-4 shadow-sm border border-gray-200">
          <div className="flex flex-col lg:flex-row lg:items-center">
            <div className="p-2 bg-blue-100 rounded-lg mb-2 lg:mb-0 lg:mr-4">
              <svg className="w-5 h-5 lg:w-6 lg:h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
            </div>
            <div className="text-center lg:text-left">
              <p className="text-xs lg:text-sm font-medium text-gray-600">Total</p>
              <p className="text-lg lg:text-2xl font-bold text-gray-900">{stats.total}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-3 lg:p-4 shadow-sm border border-gray-200">
          <div className="flex flex-col lg:flex-row lg:items-center">
            <div className="p-2 bg-yellow-100 rounded-lg mb-2 lg:mb-0 lg:mr-4">
              <svg className="w-5 h-5 lg:w-6 lg:h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="text-center lg:text-left">
              <p className="text-xs lg:text-sm font-medium text-gray-600">Pendientes</p>
              <p className="text-lg lg:text-2xl font-bold text-yellow-600">{stats.pendientes}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-3 lg:p-4 shadow-sm border border-gray-200">
          <div className="flex flex-col lg:flex-row lg:items-center">
            <div className="p-2 bg-green-100 rounded-lg mb-2 lg:mb-0 lg:mr-4">
              <svg className="w-5 h-5 lg:w-6 lg:h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="text-center lg:text-left">
              <p className="text-xs lg:text-sm font-medium text-gray-600">Aprobados</p>
              <p className="text-lg lg:text-2xl font-bold text-green-600">{stats.aprobados}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-3 lg:p-4 shadow-sm border border-gray-200">
          <div className="flex flex-col lg:flex-row lg:items-center">
            <div className="p-2 bg-red-100 rounded-lg mb-2 lg:mb-0 lg:mr-4">
              <svg className="w-5 h-5 lg:w-6 lg:h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <div className="text-center lg:text-left">
              <p className="text-xs lg:text-sm font-medium text-gray-600">Rechazados</p>
              <p className="text-lg lg:text-2xl font-bold text-red-600">{stats.rechazados}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filtros */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-3 lg:p-4">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-3">
          {/* Filtro por tipo de período */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Filtrar por
            </label>
            <select
              value={filters.dateType}
              onChange={(e) => handleFilterChange('dateType', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm"
            >
              <option value="day">Día</option>
              <option value="week">Semana</option>
              <option value="month">Mes</option>
              <option value="year">Año</option>
            </select>
          </div>

          {/* Selector de fecha */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {filters.dateType === 'day' && 'Fecha'}
              {filters.dateType === 'week' && 'Semana (seleccionar día)'}
              {filters.dateType === 'month' && 'Mes'}
              {filters.dateType === 'year' && 'Año'}
            </label>
            <input
              type={filters.dateType === 'year' ? 'number' : 
                    filters.dateType === 'month' ? 'month' : 'date'}
              value={filters.dateType === 'year' ? 
                     new Date(filters.selectedDate).getFullYear() :
                     filters.dateType === 'month' ?
                     filters.selectedDate.slice(0, 7) :
                     filters.selectedDate}
              onChange={(e) => {
                let newDate;
                if (filters.dateType === 'year') {
                  newDate = `${e.target.value}-01-01`;
                } else if (filters.dateType === 'month') {
                  newDate = `${e.target.value}-01`;
                } else {
                  newDate = e.target.value;
                }
                handleFilterChange('selectedDate', newDate);
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm"
              min={filters.dateType === 'year' ? '2020' : undefined}
              max={filters.dateType === 'year' ? '2030' : undefined}
            />
          </div>

          {/* Filtro por nombre de empleado */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Empleado
            </label>
            <input
              type="text"
              placeholder="Buscar por nombre..."
              value={filters.employeeName}
              onChange={(e) => handleFilterChange('employeeName', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm"
            />
          </div>

          {/* Filtro por estado */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Estado
            </label>
            <select
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm"
            >
              <option value="all">Todos</option>
              <option value="pendiente">Pendiente</option>
              <option value="aprobado">Aprobado</option>
              <option value="rechazado">Rechazado</option>
            </select>
          </div>

          {/* Botón para limpiar filtros */}
          <div className="flex items-end">
            <button
              onClick={() => setFilters({
                dateType: 'month',
                selectedDate: new Date().toISOString().split('T')[0],
                employeeName: '',
                status: 'all'
              })}
              className="w-full px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm font-medium"
            >
              Limpiar Filtros
            </button>
          </div>
        </div>

        {/* Información del rango seleccionado */}
        <div className="mt-3 p-2 bg-teal-50 rounded-lg">
          <div className="flex items-center space-x-2 text-sm text-teal-700">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span>
              Mostrando autorizaciones para: 
              {filters.dateType === 'day' && ` el día ${formatDateToDDMMYYYY(filters.selectedDate)}`}
              {filters.dateType === 'week' && ` la semana del ${formatDateToDDMMYYYY(getWeekRange(new Date(filters.selectedDate)).start.toISOString().split('T')[0])} al ${formatDateToDDMMYYYY(getWeekRange(new Date(filters.selectedDate)).end.toISOString().split('T')[0])}`}
              {filters.dateType === 'month' && (() => {
                // Para el filtro de mes, extraer año y mes directamente del string
                const [year, month] = filters.selectedDate.split('-');
                const monthNames = [
                  'enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
                  'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'
                ];
                return ` el mes de ${monthNames[parseInt(month) - 1]} de ${year}`;
              })()}
              {filters.dateType === 'year' && ` el año ${new Date(filters.selectedDate).getFullYear()}`}
              {filters.employeeName && ` • Empleado: "${filters.employeeName}"`}
              {filters.status !== 'all' && ` • Estado: ${filters.status}`}
            </span>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-4 lg:space-x-8 overflow-x-auto">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center space-x-2 py-2 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                activeTab === tab.id
                  ? 'border-teal-500 text-teal-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <span>{tab.icon}</span>
              <span className="hidden sm:inline">{tab.name}</span>
              <span className="sm:hidden">{tab.name.split(' ')[0]}</span>
            </button>
          ))}
        </nav>
      </div>

      {/* Content */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        {loadingAuthorizations && (
          <div className="flex items-center justify-center py-8">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-500 mx-auto"></div>
              <p className="mt-2 text-sm text-gray-600">Actualizando autorizaciones...</p>
            </div>
          </div>
        )}
        {/* Vacation Sub-tabs */}
        {activeTab === 'vacaciones' && (
          <div className="border-b border-gray-200 px-6 py-3">
            <nav className="-mb-px flex space-x-6">
              {vacationSubTabs.map((subTab) => (
                <button
                  key={subTab.id}
                  onClick={() => setVacationSubTab(subTab.id)}
                  className={`flex items-center space-x-2 py-2 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                    vacationSubTab === subTab.id
                      ? 'border-purple-500 text-purple-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <span>{subTab.icon}</span>
                  <span>{subTab.name}</span>
                </button>
              ))}
            </nav>
          </div>
        )}

        {/* Vacation Balances View */}
        {activeTab === 'vacaciones' && vacationSubTab === 'saldos' ? (
          <>
            {/* Información sobre empleados excluidos */}
            {(() => {
              const empleadosConBoletaLiquidada = employees.filter(emp => emp.es_boleta_liquidada);
              const empleadosCesados = employees.filter(emp => 
                emp.ultimo_historial && emp.ultimo_historial.fecha_cese
              );
              const totalExcluidos = empleadosConBoletaLiquidada.length + empleadosCesados.length;
              
              return totalExcluidos > 0 && (
                <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-start space-x-3">
                    <div className="flex-shrink-0">
                      <svg className="w-5 h-5 text-blue-600 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <div className="flex-1">
                      <h4 className="text-sm font-medium text-blue-900 mb-1">
                        Empleados Excluidos de Saldos de Vacaciones
                      </h4>
                      <p className="text-sm text-blue-700 mb-2">
                        {totalExcluidos} empleado{totalExcluidos !== 1 ? 's no aparecen' : ' no aparece'} en esta lista:
                      </p>
                      
                      {empleadosConBoletaLiquidada.length > 0 && (
                        <div className="mb-2">
                          <span className="text-xs font-medium text-blue-800">
                            💰 {empleadosConBoletaLiquidada.length} con vacaciones liquidadas
                          </span>
                          <details className="mt-1">
                            <summary className="text-xs text-blue-600 cursor-pointer hover:text-blue-800">
                              Ver empleados con boleta liquidada
                            </summary>
                            <div className="mt-1 text-xs text-blue-600">
                              {empleadosConBoletaLiquidada.map(emp => emp.nombre_completo).join(', ')}
                            </div>
                          </details>
                        </div>
                      )}
                      
                      {empleadosCesados.length > 0 && (
                        <div>
                          <span className="text-xs font-medium text-blue-800">
                            🚪 {empleadosCesados.length} empleado{empleadosCesados.length !== 1 ? 's cesados' : ' cesado'}
                          </span>
                          <details className="mt-1">
                            <summary className="text-xs text-blue-600 cursor-pointer hover:text-blue-800">
                              Ver empleados cesados
                            </summary>
                            <div className="mt-1 text-xs text-blue-600">
                              {empleadosCesados.map(emp => 
                                `${emp.nombre_completo} (${emp.ultimo_historial?.fecha_cese})`
                              ).join(', ')}
                            </div>
                          </details>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* Banner informativo sobre Saldo Inicial */}
            <div className="mb-6 p-4 bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-200 rounded-lg">
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-purple-500 rounded-full flex items-center justify-center">
                    <span className="text-white text-sm">⚙️</span>
                  </div>
                </div>
                <div className="flex-1">
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-2">
                    <h4 className="text-sm font-semibold text-purple-900">
                      💡 Saldo Inicial - Ajuste para Empleados Antiguos
                      {loadingInitialBalances && (
                        <span className="ml-2 text-xs text-purple-600">
                          🔄 Cargando configuraciones...
                        </span>
                      )}
                    </h4>
                    <button
                      type="button"
                      onClick={() => setImportadorSaldosOpen(true)}
                      className="inline-flex items-center justify-center px-4 py-2 text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 rounded-lg transition-colors whitespace-nowrap"
                    >
                      📥 Importar Excel
                    </button>
                  </div>
                  <div className="text-sm text-purple-800 space-y-1">
                    <p>
                      • <strong>Saldo Inicial:</strong> Permite ajustar los días de vacaciones para empleados que ingresaron antes del sistema.
                    </p>
                    <p>
                      • <strong>Funcionamiento:</strong> Los días del saldo inicial se <strong>restan de los días calculados</strong> para reducir el saldo a una cantidad realista.
                    </p>
                    <p>
                      • <strong>Ejemplo:</strong> Si un empleado tiene 60 días calculados y asignas 30 días de saldo inicial, los días correspondientes se reducen a 30 días.
                    </p>
                    <p className="text-xs text-purple-600 mt-2">
                      ✏️ <strong>Haz clic en el icono de edición</strong> en la columna "Saldo Inicial" para ajustar los días de cada empleado,
                      o usa <strong>Importar Excel</strong> para carga masiva.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Filtro por tipo de contratación */}
            <div className="mb-4 flex flex-wrap items-center gap-2">
              <span className="text-sm font-medium text-gray-700 mr-1">Tipo de contratación:</span>
              {[
                { id: 'planilla', label: 'Planilla', count: conteoModalidadVacaciones.planilla },
                { id: 'recibo', label: 'Recibo por Honorarios', count: conteoModalidadVacaciones.recibo },
                { id: 'todos', label: 'Todos', count: conteoModalidadVacaciones.todos },
              ].map((opt) => (
                <button
                  key={opt.id}
                  type="button"
                  onClick={() => setFiltroModalidadVacaciones(opt.id)}
                  className={`px-3 py-1.5 text-sm rounded-lg border transition-colors ${
                    filtroModalidadVacaciones === opt.id
                      ? opt.id === 'recibo'
                        ? 'bg-sky-600 text-white border-sky-600'
                        : 'bg-purple-600 text-white border-purple-600'
                      : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  {opt.label}
                  <span className={`ml-1.5 text-xs ${filtroModalidadVacaciones === opt.id ? 'opacity-90' : 'text-gray-500'}`}>
                    ({opt.count})
                  </span>
                </button>
              ))}
            </div>

            {vacationBalancesFiltrados.length === 0 && (
              <div className="mb-4 p-6 text-center text-sm text-gray-600 bg-gray-50 border border-dashed border-gray-300 rounded-lg">
                No hay personal en esta modalidad
                {filtroModalidadVacaciones === 'planilla' && ' (Planilla)'}
                {filtroModalidadVacaciones === 'recibo' && ' (Recibo por Honorarios)'}
                . Usa los botones de arriba para cambiar el filtro.
              </div>
            )}

            {/* Desktop Table for Balances */}
            <div className="hidden lg:block overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Empleado
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Régimen Laboral
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Fecha de Inicio
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Saldo Inicial
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Días Correspondientes
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Días Utilizados
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Saldo Pendiente
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Detalle por Años
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {vacationBalancesFiltrados.map((balance) => (
                    <tr key={balance.id} className={`hover:bg-gray-50 ${!balance.isSupported ? 'opacity-60' : ''}`}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                            balance.esRH
                              ? 'bg-gradient-to-br from-sky-500 to-sky-600'
                              : 'bg-gradient-to-br from-purple-500 to-purple-600'
                          }`}>
                            <span className="text-white font-bold text-sm">
                              {balance.name.charAt(0)}
                            </span>
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">{balance.name}</div>
                            <div className="text-sm text-gray-500 flex items-center gap-2 flex-wrap">
                              <span>{balance.position}</span>
                              <span className={`inline-flex px-1.5 py-0.5 text-[10px] font-semibold rounded ${
                                balance.esRH
                                  ? 'bg-sky-100 text-sky-800'
                                  : 'bg-purple-100 text-purple-800'
                              }`}>
                                {balance.esRH ? 'RH' : 'Planilla'}
                              </span>
                            </div>
                          </div>
                        </div>
                      </td>
                                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <div>
                          {balance.regime}
                          {!balance.isSupported && (
                            <div className="text-xs text-orange-600 font-medium">
                              ⚠️ No soportado
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <div>
                          <div className="font-medium">{formatDateToDDMMYYYY(balance.startDate)}</div>
                          {balance.yearsWorked >= 0 && (
                            <div className="text-xs text-gray-500">
                              {balance.yearsWorked} año{balance.yearsWorked !== 1 ? 's' : ''} laborado{balance.yearsWorked !== 1 ? 's' : ''}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {balance.isSupported ? (
                          <div className="flex items-center space-x-2">
                            {editingBalance === balance.id ? (
                              <div className="flex items-center space-x-1">
                                <input
                                  type="number"
                                  value={tempBalance}
                                  onChange={(e) => setTempBalance(e.target.value)}
                                  className="w-16 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                                  min="0"
                                  max="999"
                                />
                                <button
                                  onClick={() => handleSaveBalance(balance.id)}
                                  className="text-green-600 hover:text-green-800 text-sm"
                                  title="Guardar"
                                >
                                  ✓
                                </button>
                                <button
                                  onClick={handleCancelEdit}
                                  className="text-red-600 hover:text-red-800 text-sm"
                                  title="Cancelar"
                                >
                                  ✕
                                </button>
                              </div>
                            ) : (
                              <div className="flex items-center space-x-2">
                                <span className="text-sm font-medium text-purple-600">
                                  {balance.initialBalance} días
                                </span>
                                <button
                                  onClick={() => handleEditBalance(balance.id)}
                                  className="text-gray-400 hover:text-purple-600 text-sm"
                                  title="Editar saldo inicial"
                                >
                                  ✏️
                                </button>
                              </div>
                            )}
                          </div>
                        ) : (
                          <span className="text-gray-400 text-sm">-</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold">
                        {balance.isSupported ? (
                          <span className="text-blue-600">{balance.totalDays} días</span>
                        ) : (
                          <span className="text-orange-600 text-xs">Próximamente disponible</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        {balance.isSupported ? (
                          <span className="text-red-600">{balance.usedDays} días</span>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {balance.isSupported ? (
                          <span className={`inline-flex px-2 py-1 text-sm font-semibold rounded-full ${
                            balance.pendingDays > 20 ? 'bg-green-100 text-green-800' :
                            balance.pendingDays > 10 ? 'bg-yellow-100 text-yellow-800' :
                            'bg-red-100 text-red-800'
                          }`}>
                            {balance.pendingDays} días
                          </span>
                        ) : (
                          <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-orange-100 text-orange-700">
                            Próximamente
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        {balance.isSupported ? (
                          <div className="text-xs">
                            <div className="font-medium text-gray-900 mb-1">
                              {balance.yearsWorked} año{balance.yearsWorked !== 1 ? 's' : ''} trabajado{balance.yearsWorked !== 1 ? 's' : ''}
                            </div>
                            {balance.periods && balance.periods.length > 0 ? (
                              <div className="space-y-1">
                                {balance.periods.map((period, index) => (
                                  <div key={index} className="flex justify-between text-xs">
                                    <span className="text-gray-600">{period.label}:</span>
                                    <span className={`font-medium ${
                                      period.remaining > 0 ? 'text-green-700' : 'text-red-600'
                                    }`}>
                                      {period.remaining}/{period.days}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <span className="text-gray-500 text-xs">{balance.detailedBalance}</span>
                            )}
                          </div>
                        ) : (
                          <span className="text-xs text-orange-600">{balance.detailedBalance}</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile Cards for Balances */}
            <div className="lg:hidden divide-y divide-gray-200">
              {vacationBalancesFiltrados.map((balance) => (
                <div key={balance.id} className={`p-4 ${!balance.isSupported ? 'opacity-60' : ''}`}>
                  <div className="flex items-center space-x-3 mb-3">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                      balance.esRH
                        ? 'bg-gradient-to-br from-sky-500 to-sky-600'
                        : 'bg-gradient-to-br from-purple-500 to-purple-600'
                    }`}>
                      <span className="text-white font-bold text-sm">
                        {balance.name.charAt(0)}
                      </span>
                    </div>
                    <div className="flex-1">
                      <div className="text-sm font-medium text-gray-900 flex items-center gap-2 flex-wrap">
                        <span>{balance.name}</span>
                        <span className={`inline-flex px-1.5 py-0.5 text-[10px] font-semibold rounded ${
                          balance.esRH
                            ? 'bg-sky-100 text-sky-800'
                            : 'bg-purple-100 text-purple-800'
                        }`}>
                          {balance.esRH ? 'RH' : 'Planilla'}
                        </span>
                      </div>
                      <div className="text-xs text-gray-500">
                        {balance.position} • {balance.regime}
                        {!balance.isSupported && (
                          <span className="text-orange-600 font-medium"> ⚠️ No soportado</span>
                        )}
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        <span className="font-medium">Inicio:</span> {formatDateToDDMMYYYY(balance.startDate)} 
                        {balance.yearsWorked >= 0 && (
                          <span className="ml-2">
                            ({balance.yearsWorked} año{balance.yearsWorked !== 1 ? 's' : ''})
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  {balance.isSupported ? (
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-gray-500">Saldo inicial:</span>
                        <div className="flex items-center space-x-2">
                          {editingBalance === balance.id ? (
                            <div className="flex items-center space-x-1">
                              <input
                                type="number"
                                value={tempBalance}
                                onChange={(e) => setTempBalance(e.target.value)}
                                className="w-16 px-2 py-1 text-xs border border-gray-300 rounded focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                                min="0"
                                max="999"
                              />
                              <button
                                onClick={() => handleSaveBalance(balance.id)}
                                className="text-green-600 hover:text-green-800 text-xs"
                                title="Guardar"
                              >
                                ✓
                              </button>
                              <button
                                onClick={handleCancelEdit}
                                className="text-red-600 hover:text-red-800 text-xs"
                                title="Cancelar"
                              >
                                ✕
                              </button>
                            </div>
                          ) : (
                            <div className="flex items-center space-x-1">
                              <span className="font-semibold text-purple-600">{balance.initialBalance} días</span>
                              <button
                                onClick={() => handleEditBalance(balance.id)}
                                className="text-gray-400 hover:text-purple-600 text-xs"
                                title="Editar saldo inicial"
                              >
                                ✏️
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                      <div>
                        <span className="text-gray-500">Días correspondientes:</span>
                        <div className="font-semibold text-blue-600">{balance.totalDays} días</div>
                      </div>
                      <div>
                        <span className="text-gray-500">Días utilizados:</span>
                        <div className="text-red-600">{balance.usedDays} días</div>
                      </div>
                      <div className="col-span-2">
                        <span className="text-gray-500">Saldo pendiente:</span>
                        <div className={`font-semibold ${
                          balance.pendingDays > 20 ? 'text-green-600' :
                          balance.pendingDays > 10 ? 'text-yellow-600' :
                          'text-red-600'
                        }`}>
                          {balance.pendingDays} días
                        </div>
                      </div>
                      <div className="col-span-2">
                        <span className="text-gray-500">Detalle por años:</span>
                        <div className="text-sm mt-1">
                          {balance.isSupported ? (
                            <>
                              <div className="font-medium text-gray-800 mb-1">
                                {balance.yearsWorked} año{balance.yearsWorked !== 1 ? 's' : ''} trabajado{balance.yearsWorked !== 1 ? 's' : ''}
                              </div>
                              {balance.periods && balance.periods.length > 0 ? (
                                <div className="space-y-1">
                                  {balance.periods.map((period, index) => (
                                    <div key={index} className="flex justify-between text-xs">
                                      <span className="text-gray-600">{period.label}:</span>
                                      <span className={`font-medium ${
                                        period.remaining > 0 ? 'text-green-700' : 'text-red-600'
                                      }`}>
                                        {period.remaining}/{period.days}
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <span className="text-gray-500 text-xs">{balance.detailedBalance}</span>
                              )}
                            </>
                          ) : (
                            <span className="text-xs text-orange-600">{balance.detailedBalance}</span>
                          )}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-4">
                      <div className="inline-flex items-center px-3 py-2 rounded-full bg-orange-100 text-orange-700 text-sm font-medium">
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        Próximamente disponible
                      </div>
                      <p className="text-xs text-gray-500 mt-2">
                        El cálculo de vacaciones para este régimen estará disponible pronto
                      </p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </>
        ) : (
          /* Existing vacation requests or other authorization views */
          <>
            {/* Desktop Table */}
            <div className="hidden lg:block overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/5">
                      Empleado
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/6">
                      Tipo
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/6">
                      Período
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/4">
                      Motivo
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/12">
                      Estado
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/6">
                      Acciones
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filterAuthorizations().map((auth) => {
                    const employee = getEmployeeInfo(auth.employeeId);
                    const statusConfig = getStatusBadge(auth.status);
                    return (
                      <tr key={auth.id} className="hover:bg-gray-50">
                        <td className="px-3 py-3 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="w-8 h-8 bg-gradient-to-br from-teal-500 to-teal-600 rounded-full flex items-center justify-center">
                              <span className="text-white font-bold text-xs">
                                {employee?.name?.charAt(0)}
                              </span>
                            </div>
                            <div className="ml-3">
                              <div className="text-sm font-medium text-gray-900 truncate max-w-32">{employee?.name}</div>
                              <div className="text-xs text-gray-500 truncate max-w-32">{employee?.position}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-3 py-3 whitespace-nowrap">
                          <div className="flex items-center">
                            <span className="text-base mr-2">{getTypeIcon(auth.type)}</span>
                            <div>
                              <span className="text-sm text-gray-900">{getTypeLabel(auth.type, auth.subType)}</span>
                              {auth.type === 'horas-extra' && auth.subType && (
                                <div className="text-xs text-gray-500">
                                  {tiposHorasExtra.find(t => t.value === auth.subType)?.porcentaje}%
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-3 py-3 whitespace-nowrap text-sm text-gray-900">
                          <div className="text-sm">{formatDateToDDMMYYYY(auth.startDate)}</div>
                          {auth.endDate !== auth.startDate && <div className="text-xs text-gray-500">{formatDateToDDMMYYYY(auth.endDate)}</div>}
                          {auth.hours && <div className="text-xs text-gray-500">{auth.hours}h</div>}
                          {auth.days && <div className="text-xs text-gray-500">{auth.days}d</div>}
                        </td>
                        <td className="px-3 py-3">
                          <div className="text-sm text-gray-900 truncate max-w-48">{auth.reason}</div>
                          {auth.description && <div className="text-xs text-gray-500 truncate max-w-48">{auth.description}</div>}
                        </td>
                        <td className="px-3 py-3 whitespace-nowrap">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${statusConfig.class}`}>
                            {statusConfig.text}
                          </span>
                        </td>
                        <td className="px-3 py-3 whitespace-nowrap text-sm text-gray-500">
                          {/* Mostrar botones de acción para todos los tipos de autorizaciones */}
                          <div className="flex flex-col space-y-1">
                              <button
                                onClick={() => handleApprove(auth.id)}
                              className={`px-2 py-1 text-xs font-medium rounded transition-colors ${
                                auth.status === 'aprobado' 
                                  ? 'bg-green-100 text-green-800 cursor-default' 
                                  : 'bg-green-50 text-green-600 hover:bg-green-100 hover:text-green-800'
                              }`}
                              disabled={auth.status === 'aprobado'}
                              title={auth.status === 'aprobado' ? 'Aprobado' : 'Aprobar'}
                            >
                              ✓
                            </button>
                            <button
                              onClick={() => handlePending(auth.id)}
                              className={`px-2 py-1 text-xs font-medium rounded transition-colors ${
                                auth.status === 'pendiente' 
                                  ? 'bg-yellow-100 text-yellow-800 cursor-default' 
                                  : 'bg-yellow-50 text-yellow-600 hover:bg-yellow-100 hover:text-yellow-800'
                              }`}
                              disabled={auth.status === 'pendiente'}
                              title={auth.status === 'pendiente' ? 'Pendiente' : 'Marcar como Pendiente'}
                            >
                              ⏳
                              </button>
                              <button
                                onClick={() => handleReject(auth.id)}
                              className={`px-2 py-1 text-xs font-medium rounded transition-colors ${
                                auth.status === 'rechazado' 
                                  ? 'bg-red-100 text-red-800 cursor-default' 
                                  : 'bg-red-50 text-red-600 hover:bg-red-100 hover:text-red-800'
                              }`}
                              disabled={auth.status === 'rechazado'}
                              title={auth.status === 'rechazado' ? 'Rechazado' : 'Rechazar'}
                            >
                              ✗
                              </button>
                              {/* Botón de descarga de constancia - Para todas las autorizaciones aprobadas */}
                              {auth.status === 'aprobado' && (
                                <button
                                  onClick={() => handleDownloadConstancia(auth)}
                                  className="px-2 py-1 text-xs font-medium rounded transition-colors bg-blue-50 text-blue-600 hover:bg-blue-100 hover:text-blue-800"
                                  title="Descargar Constancia"
                                >
                                  📄
                                </button>
                              )}
                            </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Mobile Cards */}
            <div className="lg:hidden divide-y divide-gray-200">
              {filterAuthorizations().map((auth) => {
                const employee = getEmployeeInfo(auth.employeeId);
                const statusConfig = getStatusBadge(auth.status);
                return (
                  <div key={auth.id} className="p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-gradient-to-br from-teal-500 to-teal-600 rounded-full flex items-center justify-center">
                          <span className="text-white font-bold text-sm">
                            {employee?.name?.charAt(0)}
                          </span>
                        </div>
                        <div>
                          <div className="text-sm font-medium text-gray-900">{employee?.name}</div>
                          <div className="text-xs text-gray-500">{employee?.position}</div>
                        </div>
                      </div>
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${statusConfig.class}`}>
                        {statusConfig.text}
                      </span>
                    </div>
                    
                    <div className="space-y-2 text-sm">
                      <div className="flex items-center">
                        <span className="text-lg mr-2">{getTypeIcon(auth.type)}</span>
                        <div>
                          <span className="font-medium">{getTypeLabel(auth.type, auth.subType)}</span>
                          {auth.type === 'horas-extra' && auth.subType && (
                            <div className="text-xs text-gray-500">
                              {tiposHorasExtra.find(t => t.value === auth.subType)?.porcentaje}% - {tiposHorasExtra.find(t => t.value === auth.subType)?.descripcion}
                            </div>
                          )}
                        </div>
                      </div>
                      <div>
                        <span className="text-gray-500">Período:</span>
                        <span className="ml-2">{formatDateToDDMMYYYY(auth.startDate)} {auth.endDate !== auth.startDate && `- ${formatDateToDDMMYYYY(auth.endDate)}`}</span>
                      </div>
                      {auth.hours && (
                        <div>
                          <span className="text-gray-500">Horas:</span>
                          <span className="ml-2">{auth.hours}h</span>
                        </div>
                      )}
                      {auth.days && (
                        <div>
                          <span className="text-gray-500">Días:</span>
                          <span className="ml-2">{auth.days} días</span>
                        </div>
                      )}
                      <div>
                        <span className="text-gray-500">Motivo:</span>
                        <span className="ml-2">{auth.reason}</span>
                      </div>
                      {auth.description && (
                        <div>
                          <span className="text-gray-500">Descripción:</span>
                          <span className="ml-2">{auth.description}</span>
                        </div>
                      )}
                    </div>

                    {/* Mostrar botones de acción para todos los tipos de autorizaciones en móvil */}
                    <div className="flex space-x-1 mt-3 pt-3 border-t border-gray-200">
                        <button
                          onClick={() => handleApprove(auth.id)}
                        className={`flex-1 px-2 py-2 text-xs rounded-lg font-medium transition-colors ${
                          auth.status === 'aprobado' 
                            ? 'bg-green-100 text-green-800 cursor-default' 
                            : 'bg-green-50 text-green-700 hover:bg-green-100'
                        }`}
                        disabled={auth.status === 'aprobado'}
                      >
                        {auth.status === 'aprobado' ? '✓ Aprobado' : 'Aprobar'}
                      </button>
                      <button
                        onClick={() => handlePending(auth.id)}
                        className={`flex-1 px-2 py-2 text-xs rounded-lg font-medium transition-colors ${
                          auth.status === 'pendiente' 
                            ? 'bg-yellow-100 text-yellow-800 cursor-default' 
                            : 'bg-yellow-50 text-yellow-700 hover:bg-yellow-100'
                        }`}
                        disabled={auth.status === 'pendiente'}
                      >
                        {auth.status === 'pendiente' ? '⏳ Pendiente' : 'Pendiente'}
                        </button>
                        <button
                          onClick={() => handleReject(auth.id)}
                        className={`flex-1 px-2 py-2 text-xs rounded-lg font-medium transition-colors ${
                          auth.status === 'rechazado' 
                            ? 'bg-red-100 text-red-800 cursor-default' 
                            : 'bg-red-50 text-red-700 hover:bg-red-100'
                        }`}
                        disabled={auth.status === 'rechazado'}
                      >
                        {auth.status === 'rechazado' ? '✗ Rechazado' : 'Rechazar'}
                        </button>
                        {/* Botón de descarga de constancia - Para todas las autorizaciones aprobadas en móvil */}
                        {auth.status === 'aprobado' && (
                          <button
                            onClick={() => handleDownloadConstancia(auth)}
                            className="flex-1 px-2 py-2 text-xs rounded-lg font-medium transition-colors bg-blue-50 text-blue-700 hover:bg-blue-100"
                            title="Descargar Constancia"
                          >
                            📄 Constancia
                          </button>
                        )}
                      </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-4 lg:p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg lg:text-xl font-semibold text-gray-900">Nueva Autorización</h3>
                <button
                  onClick={resetModal}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            
            <form onSubmit={handleSubmit} className="p-4 lg:p-6 space-y-6">
              {/* Primer paso: Tipo y Fecha de inicio */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Tipo *
                  </label>
                  <select
                    name="type"
                    value={newAuthorization.type}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                  >
                    <option value="horas-extra">Horas Extra</option>
                    <option value="permiso">Permiso</option>
                    <option value="suspension">Suspensión</option>
                    <option value="descanso-medico">Descanso Médico</option>
                    <option value="subsidio-por-enfermedad">Subsidio por Enfermedad</option>
                    <option value="licencia-con-goce-de-haber">Licencia con Goce de Haber</option>
                    <option value="vacaciones">Vacaciones</option>
                  </select>
              </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Fecha de inicio *
                  </label>
                  <input
                    type="date"
                    name="startDate"
                    value={newAuthorization.startDate}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                  />
                </div>
                </div>
                
              {/* Campo específico para tipo de horas extra */}
              {newAuthorization.type === 'horas-extra' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Tipo de Horas Extra *
                  </label>
                  <select
                    name="subType"
                    value={newAuthorization.subType}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                  >
                    {tiposHorasExtra.map((tipo) => (
                      <option key={tipo.value} value={tipo.value}>
                        {tipo.label}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    {newAuthorization.subType && (
                      <>
                        Recargo del {tiposHorasExtra.find(t => t.value === newAuthorization.subType)?.porcentaje}% sobre el salario base
                      </>
                    )}
                  </p>
                </div>
              )}

              {/* Segundo paso: Empleado (filtrado por fecha) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Empleado *
                  {!newAuthorization.startDate && (
                    <span className="text-xs text-gray-500 ml-2">(Selecciona primero la fecha de inicio)</span>
                  )}
                </label>
                <select
                  name="employeeId"
                  value={newAuthorization.employeeId}
                  onChange={handleInputChange}
                  required
                  disabled={!newAuthorization.startDate}
                  className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 ${
                    !newAuthorization.startDate ? 'bg-gray-100 cursor-not-allowed' : ''
                  }`}
                >
                  <option value="">
                    {!newAuthorization.startDate ? 'Primero selecciona la fecha de inicio' : 'Seleccionar empleado activo'}
                  </option>
                  {newAuthorization.startDate && filterEmployeesByDate(newAuthorization.startDate).map((employee) => {
                    const fechaIngreso = employee.ultimo_historial?.fecha_ingreso;
                    const fechaFormateada = fechaIngreso
                      ? formatDateToDDMMYYYY(fechaIngreso)
                      : 'sin fecha de ingreso en historial';
                    console.log('🗓️ Formateando fecha para dropdown:', {
                      empleado: employee.nombre_completo,
                      fechaOriginal: fechaIngreso,
                      fechaFormateada: fechaFormateada
                    });
                    
                    return (
                      <option key={employee.id} value={employee.id}>
                        {employee.nombre_completo} - {employee.cargo}
                        {fechaIngreso
                          ? ` (Activo desde ${fechaFormateada})`
                          : ' (Activo — sin historial laboral)'}
                      </option>
                    );
                  })}
                </select>
                {newAuthorization.startDate && filterEmployeesByDate(newAuthorization.startDate).length === 0 && (
                  <p className="text-xs text-yellow-600 mt-1">
                    No hay empleados activos para la fecha seleccionada
                  </p>
                )}
                
                {/* Mensaje informativo sobre empleados disponibles */}
                {newAuthorization.startDate && filterEmployeesByDate(newAuthorization.startDate).length > 0 && (
                  <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded-md">
                    <p className="text-xs text-green-700">
                      <strong>✅ Empleados disponibles:</strong> {filterEmployeesByDate(newAuthorization.startDate).length} empleado{filterEmployeesByDate(newAuthorization.startDate).length !== 1 ? 's' : ''} 
                      {filterEmployeesByDate(newAuthorization.startDate).length !== 1 ? ' están' : ' está'} activo{filterEmployeesByDate(newAuthorization.startDate).length !== 1 ? 's' : ''} para la fecha seleccionada.
                    </p>
                  </div>
                )}
              </div>

              {/* Tercer paso: Fechas y duración - Solo para permisos, vacaciones, descansos médicos, subsidios y licencias con goce */}
              {(newAuthorization.type === 'permiso' || newAuthorization.type === 'suspension' || newAuthorization.type === 'vacaciones' || 
                newAuthorization.type === 'descanso-medico' || newAuthorization.type === 'subsidio-por-enfermedad' || newAuthorization.type === 'licencia-con-goce-de-haber') && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Fecha de fin *
                  </label>
                  <input
                    type="date"
                    name="endDate"
                    value={newAuthorization.endDate}
                    onChange={handleInputChange}
                      required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                  />
                </div>
                  
                  {/* Días calculados automáticamente */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Días {newAuthorization.type === 'vacaciones' ? '*' : ''}
                      <span className="text-xs text-gray-500 ml-2">(Calculado automáticamente)</span>
                    </label>
                    <input
                      type="number"
                      name="days"
                      value={newAuthorization.days}
                      readOnly
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 cursor-not-allowed focus:outline-none"
                      placeholder="Se calculará automáticamente"
                      min="0"
                    />
              </div>
                </div>
              )}

              {/* Selector de días de reposo médico - Solo para descansos médicos */}
              {newAuthorization.type === 'descanso-medico' && newAuthorization.startDate && newAuthorization.endDate && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Días de Reposo Médico (0 horas)
                      <span className="text-xs text-gray-500 ml-2">
                        Selecciona los días que serán reposo (0 horas) vs descanso médico (8 horas)
                      </span>
                    </label>
                    <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                      <p className="text-sm text-orange-700 mb-3">
                        <strong>🏥 Descanso Médico:</strong> 8 horas (días normales) | 
                        <strong className="ml-2">🛏️ Reposo Médico:</strong> 0 horas (días de descanso del trabajador)
                      </p>
                      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-2">
                        {getDaysInRange(newAuthorization.startDate, newAuthorization.endDate).map((day) => (
                          <div key={day.date} className="text-center">
                            <button
                              type="button"
                              onClick={() => toggleReposoDay(day.date)}
                              className={`w-full px-2 py-2 rounded-lg border-2 transition-all duration-200 text-xs font-medium ${
                                newAuthorization.diasReposoMedico.includes(day.date)
                                  ? 'bg-orange-100 border-orange-300 text-orange-800'
                                  : 'bg-white border-gray-200 text-gray-700 hover:border-orange-200 hover:bg-orange-50'
                              }`}
                            >
                              <div className="flex flex-col items-center">
                                <span className="text-xs font-semibold">{day.dayName}</span>
                                <span className="text-lg font-bold">{day.dayNumber}</span>
                                <span className="text-xs mt-1">
                                  {newAuthorization.diasReposoMedico.includes(day.date) ? '🛏️ 0h' : '🏥 8h'}
                                </span>
                              </div>
                            </button>
                          </div>
                        ))}
                      </div>
                      <div className="mt-3 text-xs text-gray-600">
                        <strong>Resumen:</strong> {getDaysInRange(newAuthorization.startDate, newAuthorization.endDate).length - newAuthorization.diasReposoMedico.length} días de descanso médico (8h) + {newAuthorization.diasReposoMedico.length} días de reposo médico (0h)
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Campo de horas y motivo para horas extra */}
              {newAuthorization.type === 'horas-extra' && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Horas *
                    </label>
                    <input
                      type="number"
                      name="hours"
                      value={newAuthorization.hours}
                      onChange={handleInputChange}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                      placeholder="0"
                      min="0"
                      step="0.5"
                    />
                  </div>
                
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Motivo *
                    </label>
                    <input
                      type="text"
                      name="reason"
                      value={newAuthorization.reason}
                      onChange={handleInputChange}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                      placeholder="Motivo de la autorización"
                    />
                  </div>
                  </div>
                )}
                
              {/* Campo de motivo en ancho completo para otros tipos */}
              {newAuthorization.type !== 'horas-extra' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Motivo *
                  </label>
                  <input
                    type="text"
                    name="reason"
                    value={newAuthorization.reason}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                    placeholder="Motivo de la autorización"
                  />
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Descripción
                </label>
                <textarea
                  name="description"
                  value={newAuthorization.description}
                  onChange={handleInputChange}
                  rows="3"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                  placeholder="Detalles adicionales..."
                />
              </div>

              <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-end space-y-4 sm:space-y-0 sm:space-x-4 pt-6 border-t border-gray-200">
                <button
                  type="button"
                  onClick={resetModal}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors duration-200"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className={`px-6 py-2 text-sm font-medium text-white rounded-lg transition-all duration-200 ${
                    submitting 
                      ? 'bg-gray-400 cursor-not-allowed' 
                      : 'bg-gradient-to-r from-teal-500 to-teal-600 hover:from-teal-600 hover:to-teal-700'
                  }`}
                >
                  {submitting ? (
                    <div className="flex items-center space-x-2">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      <span>Creando...</span>
                    </div>
                  ) : (
                    'Crear Autorización'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <ImportadorSaldosVacaciones
        isOpen={importadorSaldosOpen}
        onClose={() => setImportadorSaldosOpen(false)}
        empresaId={selectedCompany?.id}
        empleados={employees}
        saldosActuales={initialBalances}
        onImportado={async () => {
          await loadInitialBalances();
        }}
      />
    </div>
  );
};

export default Autorizaciones; 